import React from 'react';
import PropTypes from 'prop-types';

import withStyles from '@mui/styles/withStyles';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';

import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import DepotGestionSelect from 'components/Shared/DepotGestionSelect';
import DropZone from 'components/Shared/DropZone';
import AideMessage from 'components/Shared/AideMessage';
import LigneGestionQualification from 'components/Shared/LigneGestionQualification';
import LoadingButton from 'components/Shared/LoadingButton';
import MessageDialog from 'components/Shared/MessageDialog';
import PieceGestionSearch from 'components/Shared/PieceGestionSearch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import InfoIcon from '@mui/icons-material/InfoOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import QualificationAccordionSummary from 'components/Screens/AddReclamationDialog/AddReclamationForm/Steps/QualitificationStepFactory/QualificationComponents/QualificationAccordionSummary';

import { ReclamationQualificationDetailType } from 'constants.js';
import { ReclamationQualificationDetailCodes } from 'constants.js';
import { AnomalieType } from 'constants.js';
import { EligibiliteRetourVolontaire, ExclusionFraisRetourVolontaire, RemboursementType, RepriseMarchandiseType, AutorisationRemboursementEnMagasin } from 'constants.js';
import { TypePiece } from 'constants.js';
import { AdresseDestinations } from 'constants.js';
import { KitType } from 'constants.js';

import qualificationHelper from 'utils/qualificationHelper';
import stringHelper from 'utils/stringHelper';
import { getMessageAutorisationRemboursementEnMagasin, getResultatRemboursement } from 'utils/clientHelper';

import MultiSelectMailContactClient from 'components/Shared/MultiSelectMailContactClient';
import MyDatePicker from 'components/Shared/MyDatePicker';
import { sansDepotMagasin } from 'utils/depotHelper';

const styles = theme => ({
    flexContainer: {
        display: 'flex',
        flexFlow: 'row wrap',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    row: {
        flexBasis: '100%',
        marginBottom: '10px'
    },
    formControl: {
        margin: theme.spacing(1)
    },
    group: {
        margin: theme.spacing(1, 0)
    },
    paper: {
        padding: theme.spacing(1),
        flexGrow: 1
    },
    card: {
        height: '100%',
        border: theme.palette.primary.main + ' 1px solid',
        transition: 'border-color 1s',
        '&:hover': {
            border: theme.palette.secondary[500] + ' 1px solid',
        }
    }
});

const Etape = {
    ChoixDeLaPiece: 0,
    ChoixDesLignes: 1,
    EligibiliteResult: 2,
    TraitementRetour: 3,
    DecoteTotal: 4
};

const modeRetourLabel = "Veuillez sélectionner le mode de retour souhaité. ";
const modeRetourLabels = [
    "Un transporteur doit venir à cette adresse",  // Non disponible si décôte totale
    "Le client dépose la marchandise dans un magasin",
    "Le client souhaite prendre en charge lui-même le retour plateforme",
    "Le client remet la marchandise à l'ATC"
];

const modePriseEnChargeLabel = "Veuillez sélectionner le mode de prise en charge. ";
const modePriseEnChargeLabels = [
    "Le client s'occupe lui-même du transport",
    "Foussier envoie une étiquette transporteur pré-payée (Express uniquement)"
];

const modePriseEnChargeATCLabel = "Veuillez sélectionner le mode de prise en charge. ";
const modePriseEnChargeATCLabels = [
    "L'ATC déposera la marchandise au magasin",
    "L'ATC effectuera le retour directement à la plateforme de départ"
];

const clientInformeLabel = "J'ai informé le client des conditions de reprise.";
const clientInformeEligibiliteNonLabel = "J'ai informé le client des conditions de reprise.";

// Labels pour l'éligibilité
const eligibiliteNonCauseRapidoLabel = "Cette commande est une commande sur mesure. Elle ne peut pas être retournée et ne pas faire l'objet d'un avoir.";
const eligibiliteNonCauseLivraisonDirecteLabel = "Cette commande est une commande en livraison directe de notre fournisseur. Elle ne peut donc pas être retournée sans validation du fournisseur.";
const eligibiliteNonCauseTRLabel = "Certains articles de cette commande ont été commandés spécialement, ils ne peuvent donc  pas être retournés sans validation de notre fournisseur.";
const eligibiliteNonArticleExcluLabel = "Certains articles de cette commande ne peuvent pas être retournés.";
const eligibiliteNonMontantFaibleLabel = "Le montant minimum autorisé pour un retour n'est pas atteint. Ces articles ne peuvent pas être retournés et ne feront donc pas l'objet d'un avoir.";
const eligibiliteNonCauseClientFermeLabel = "Ce client est fermé.";
const eligibiliteNonQuantiteRetournableDepasseeLabel = "La quantité à retourner dépasse la quantité retournable d'au moins une ligne de la commande. Réduisez les quantités avant de poursuivre.";

const eligibiliteOkSeulementMagasinLabel = (flags) => 
{
    var result = "Vous pouvez toutefois poursuivre l'enregistrement qui conduira à la reprise des marchandises dans votre stock.";

    if(flags & 1)
    {
        result = "Le montant minimum retourné n'est pas atteint.</br>" + result;
    }
    if(flags & 2)
    {
        result = "Présence d'au moins un article périssable.</br>" + result;
    }
    // if(flags & 4)
    // {

    // }

    return result;
}



const messageTooltipInfoFormulaireRetour = "Certains choix du formulaire sont bloqués du fait de la typologie du client et du montant qui est inférieur au montant minimum de retour ou de la présence d'article périssable.";

const qualificationDetailLabels =
{
    [ReclamationQualificationDetailCodes.Poids]: "Poids théorique (en g)",
    [ReclamationQualificationDetailCodes.NombreColis]: "Nombre de colis",
    [ReclamationQualificationDetailCodes.NombrePalettes]: "Nombre de palette",
    [ReclamationQualificationDetailCodes.NombreLongueurs]: "Nombre de longueur > 1,20 m",
    [ReclamationQualificationDetailCodes.DateEnlevement]: "Date d'enlèvement souhaitée",
};

const messageErreurQuantiteRebut = 'La quantité renseignée doit être supérieure à la quantité déclarée en retour';

const typeRemboursementLabels = [
    "CB",
    "Especes",
    "Virement",
    "Deduction fin de mois",
    "Carte achat"
]

const typeRepriseMarchandiseLabels = [
    "Je garde la marchandise dans mon stock", // Sur le magasin
    "PF Monné", // Transfert PF
    "PF Monné"  // En plateforme pour avoir
]



const blocRemboursementManagement = (param) => {

    const { autorisation, montant, rebutValider, plvMagasin, remboursementType, eligibilite, erreurKit } = param;

    const eligibiliteSeulementMagasin = eligibilite === EligibiliteRetourVolontaire.OKSeulementMagasin; // Moins de 15€ ou présence de périssable avec utilisateur magasin

    console.log('blocRemboursementManagement', { ...param, eligibiliteSeulementMagasin })

    let result = {
        remboursement:
            {
                disabledCB: autorisation !== AutorisationRemboursementEnMagasin.Autorise,
                disabledEspeces: (montant > 100) || autorisation !== AutorisationRemboursementEnMagasin.Autorise, // Toujours interdire les espèces si > 100 €
                disabledVirement: autorisation === AutorisationRemboursementEnMagasin.NonAutorise || autorisation === AutorisationRemboursementEnMagasin.CarteAchat,
                disabledCarteAchat: autorisation !== AutorisationRemboursementEnMagasin.CarteAchat,
                showDeductionFinDeMoisFDM: autorisation === AutorisationRemboursementEnMagasin.NonAutorise // Déduction fin de mois, seul choix possible pour les client non autorisé à être remboursé en magasin
            },
        reprise: {
            disabledMagasin: false,
            disabledTransfert: false,
            disabledPlateformeAvoir: false,
            showMessageProduitStocke: false,
        }
    };


    // A ce stade, tout le bloc remboursement est géré !


    // REPRISE

    // Reprise en magasin interdite si compta uniquement
    result.reprise.disabledMagasin = !eligibiliteSeulementMagasin && // Si OKSeulementMagasin, on peut reprendre en magasin
                                     (     autorisation === AutorisationRemboursementEnMagasin.ParComptaUniquement
                                        || autorisation === AutorisationRemboursementEnMagasin.ParComptaUniquementMPU
                                        || autorisation === AutorisationRemboursementEnMagasin.CarteAchat
                                        || erreurKit
                                     );


    // Transfert - il faut déjà être autorisé au remboursement
    // De plus on ne peut pas faire de transfert si le montant n'est pas atteint, si les articles sont au plan de vente ou s'il y a des rebuts
    result.reprise.disabledTransfert =
           autorisation !== AutorisationRemboursementEnMagasin.Autorise
        || eligibiliteSeulementMagasin
        || plvMagasin
        || rebutValider
        || remboursementType === RemboursementType.Virement
        || erreurKit;



    // En plateforme pour avoir

    switch (autorisation) {
        case AutorisationRemboursementEnMagasin.NonAutorise:
            {
                if (   eligibiliteSeulementMagasin
                    || plvMagasin
                    || rebutValider
                    || remboursementType === RemboursementType.Cb
                    || remboursementType === RemboursementType.Especes
                )
                {
                    result.reprise.disabledPlateformeAvoir = true;
                }
                break;
            }
        case AutorisationRemboursementEnMagasin.Autorise:
            {
                if (   eligibiliteSeulementMagasin
                    || plvMagasin
                    || rebutValider
                    || remboursementType === RemboursementType.Cb
                    || remboursementType === RemboursementType.Especes
                ) {
                    result.reprise.disabledPlateformeAvoir = true;
                }
                break;
            }
        case AutorisationRemboursementEnMagasin.ParComptaUniquement:
            {
                // Logiquement, le seul choix possible
                break;
            }
        case AutorisationRemboursementEnMagasin.ParComptaUniquementMPU:
            {
                // Logiquement, le seul choix possible
                break;
            }
        case AutorisationRemboursementEnMagasin.CarteAchat:
            {
                // Logiquement, le seul choix possible
                break;
            }
    }

    // Affichage du message "Produit stocké en magasin à remettre en stock, interdiction de renvoyer au Monné"
    // Au plan de vente et reprise uniquement au magasin
    result.reprise.showMessageProduitStocke = plvMagasin && !result.reprise.disabledMagasin && result.reprise.disabledTransfert && result.reprise.disabledPlateformeAvoir;

    //console.log("blocRemboursementManagement", param, result);

    return result;
};




class DemandeRetourMarchandiseLivreeConformeStep extends React.Component {

    constructor(props) {
        super(props);

        this.state = {

            expandedPanel: 'panelPiece',
            // DEV-4012 : même avec une pièce pré-remplie dans la recherche, on reste à ChoixDeLaPiece :
            // l'utilisateur doit cliquer la pièce pour passer par le choix « Type de retour » (BL/Commande),
            // qui hydrate pieceSelectionnee et déclenche le chargement des lignes.
            etape: Etape.ChoixDeLaPiece,
            eligibilite: EligibiliteRetourVolontaire.OK,
            seulementMagasinFlags: 0,

            // DEV-4012 : type de retour choisi pour la pièce sélectionnée.
            // null = pas encore choisi, TypePiece.BonLivraison ou TypePiece.Commande sinon.
            typeRetour: null,
            // Pièce (BL) sélectionnée dans la liste, conservée pour résoudre commandeOrigine/commandeGestionCle/codeDepot.
            pieceSelectionnee: null,

            eligibiliteNonMontantFaibleDialogOpen: false,
            eligibiliteOkSeulementMagasinDialogOpen: false,

            adresseEnlevementNom: this.props.adresse && this.props.adresse.nomLivraison ? this.props.adresse.nomLivraison : '',
            adresseEnlevementAdresse1: this.props.adresse && this.props.adresse.adresse1 ? this.props.adresse.adresse1 : '',
            adresseEnlevementAdresse2: this.props.adresse && this.props.adresse.adresse2 ? this.props.adresse.adresse2 : '',
            adresseEnlevementCodePostal: this.props.adresse && this.props.adresse.codePostal ? this.props.adresse.codePostal : '',
            adresseEnlevementVille: this.props.adresse && this.props.adresse.ville ? this.props.adresse.ville : '',

            donneurOrdreMail: this.props.donneurOrdre && this.props.donneurOrdre.mail ? this.props.donneurOrdre.mail : '',
            donneurOrdreNom: this.props.donneurOrdre && this.props.donneurOrdre.nom ? this.props.donneurOrdre.nom : '',
            donneurOrdreTelephone: this.props.donneurOrdre && this.props.donneurOrdre.telephoneMobile ? this.props.donneurOrdre.telephoneMobile : this.props.donneurOrdre && this.props.donneurOrdre.telephoneFixe ? this.props.donneurOrdre.telephoneFixe : '',

            [ReclamationQualificationDetailCodes.Poids]: this.props.poidsTheorique,

            [ReclamationQualificationDetailCodes.NombreColis]: 0,
            [ReclamationQualificationDetailCodes.NombrePalettes]: 0,
            [ReclamationQualificationDetailCodes.NombreLongueurs]: 0,

            [ReclamationQualificationDetailCodes.DateEnlevement]: null,

            adresseDestinationCode: '',

            adresseDestinationNom: '',
            adresseDestinationAdresse1: '',
            adresseDestinationAdresse2: '',
            adresseDestinationCodePostal: '',
            adresseDestinationVille: '',

            warningFraisRetour: false,

            ignorerDecoteTotale: false,
            controleEligibiliteEnCours: false,

            articlesEnEtatRevente: null,
            declaratifRebut: [],
            remboursementType: null,
            repriseMarchandise: null,
            rebutValider: false,
            plvMagasin: false
        };
    }

    componentDidUpdate(prevProps) {
        // Typical usage (don't forget to compare props):
        if (this.props.reclamation.numeroPiece !== prevProps.reclamation.numeroPiece) {
            this.setState({
                adresseEnlevementNom: this.props.adresse && this.props.adresse.nomLivraison ? this.props.adresse.nomLivraison : '',
                adresseEnlevementAdresse1: this.props.adresse && this.props.adresse.adresse1 ? this.props.adresse.adresse1 : '',
                adresseEnlevementAdresse2: this.props.adresse && this.props.adresse.adresse2 ? this.props.adresse.adresse2 : '',
                adresseEnlevementCodePostal: this.props.adresse && this.props.adresse.codePostal ? this.props.adresse.codePostal : '',
                adresseEnlevementVille: this.props.adresse && this.props.adresse.ville ? this.props.adresse.ville : '',

                donneurOrdreMail: this.props.donneurOrdre && this.props.donneurOrdre.mail ? this.props.donneurOrdre.mail : '',
                donneurOrdreNom: this.props.donneurOrdre && this.props.donneurOrdre.nom ? this.props.donneurOrdre.nom : '',
                donneurOrdreTelephone: this.props.donneurOrdre && this.props.donneurOrdre.telephoneMobile ? this.props.donneurOrdre.telephoneMobile : this.props.donneurOrdre && this.props.donneurOrdre.telephoneFixe ? this.props.donneurOrdre.telephoneFixe : '',

                [ReclamationQualificationDetailCodes.Poids]: this.props.poidsTheorique,

                adresseDestinationCode: '',

                [ReclamationQualificationDetailCodes.NombreColis]: 0,
                [ReclamationQualificationDetailCodes.NombrePalettes]: 0,
                [ReclamationQualificationDetailCodes.NombreLongueurs]: 0,

                [ReclamationQualificationDetailCodes.DateEnlevement]: null,

                warningFraisRetour: false,

                ignorerDecoteTotale: false,

                articlesEnEtatRevente: null,
                declaratifRebut: [],
                remboursementType: null,
                repriseMarchandise: null,
                rebutValider: false,
                plvMagasin: false
            });
        }

        if (this.props.adresse !== null && prevProps.adresse === null) {
            this.setState({
                adresseEnlevementNom: this.props.adresse && this.props.adresse.nomLivraison ? this.props.adresse.nomLivraison : '',
                adresseEnlevementAdresse1: this.props.adresse && this.props.adresse.adresse1 ? this.props.adresse.adresse1 : '',
                adresseEnlevementAdresse2: this.props.adresse && this.props.adresse.adresse2 ? this.props.adresse.adresse2 : '',
                adresseEnlevementCodePostal: this.props.adresse && this.props.adresse.codePostal ? this.props.adresse.codePostal : '',
                adresseEnlevementVille: this.props.adresse && this.props.adresse.ville ? this.props.adresse.ville : ''
            });
        }

        if (this.props.poidsTheorique !== prevProps.poidsTheorique) {
            this.setState({
                [ReclamationQualificationDetailCodes.Poids]: this.props.poidsTheorique,
            });
        }
    }

    componentDidMount() {
        // On détermine si on doit rendre accessible le bouton pour passer à l'étape suivante ou le bouton de création.
        const { reclamation, onNextStepValid } = this.props;

        const nextStepValid = reclamation.numeroPiece.length > 0 && reclamation.lignes.length > 0;

        onNextStepValid(nextStepValid);
    }

    adresseEnlevementToStr = ({ adresseEnlevementNom, adresseEnlevementAdresse1, adresseEnlevementAdresse2, adresseEnlevementCodePostal, adresseEnlevementVille }) => {

        let adresseStr = adresseEnlevementNom;
        if (adresseEnlevementAdresse1 && adresseEnlevementAdresse1 !== '') {
            adresseStr += '\r\n' + adresseEnlevementAdresse1;
        }
        if (adresseEnlevementAdresse2 && adresseEnlevementAdresse2 !== '') {
            adresseStr += '\r\n' + adresseEnlevementAdresse2;
        }
        adresseStr += '\r\n' + adresseEnlevementCodePostal + ' ' + adresseEnlevementVille;

        return adresseStr;
    }

    adresseDestinationToStr = ({ adresseDestinationCode, adresseDestinationNom, adresseDestinationAdresse1, adresseDestinationAdresse2, adresseDestinationCodePostal, adresseDestinationVille }) => {

        if (adresseDestinationCode === 3) {

            let adresseStr = adresseDestinationNom;
            if (adresseDestinationAdresse1 && adresseDestinationAdresse1 !== '') {
                adresseStr += '\r\n' + adresseDestinationAdresse1;
            }
            if (adresseDestinationAdresse2 && adresseDestinationAdresse2 !== '') {
                adresseStr += '\r\n' + adresseDestinationAdresse2;
            }
            adresseStr += '\r\n' + adresseDestinationCodePostal + ' ' + adresseDestinationVille;

            return adresseStr;
        }

        let adresse = AdresseDestinations.find(x => x.id === adresseDestinationCode);

        if (adresse) {
            return adresse.nom;
        }

        return '';
    }

    donneurOrdreToStr = ({ donneurOrdreNom, donneurOrdreMail, donneurOrdreTelephone }) => {
        return donneurOrdreNom + ' / ' + donneurOrdreMail + ' / ' + donneurOrdreTelephone;
    }

    // Obtention d'un détail quelconque.
    getDetail(code, typeValeur) {
        const { reclamation } = this.props;

        // Initialisation de la valeur
        let valeur = '';

        // Obtention du code
        const detail = reclamation.qualificationDetails.find(detail => detail.code === code);

        // Affectation si possible de la valeur en fonction du type demandé
        if (detail !== undefined) {
            valeur = detail[typeValeur];
        }

        return valeur;
    }


    getModeRetour() {
        const { reclamation } = this.props;

        let modeRetour = '';

        const detailModeRetour = reclamation.qualificationDetails.find(detail => detail.code === ReclamationQualificationDetailCodes.ModeRetour);
        if (detailModeRetour !== undefined) {
            modeRetour = detailModeRetour.valeurInteger;
        }

        return modeRetour;
    }

    getCodeDepot() {
        const { reclamation } = this.props;

        let depot = '';

        const detailDepot = reclamation.qualificationDetails.find(detail => detail.code === ReclamationQualificationDetailCodes.Depot);
        if (detailDepot !== undefined) {
            depot = detailDepot.valeurString;
        }

        return depot;
    }

    getDepot() {
        const { reclamation } = this.props;

        let depot = '';

        const detailDepot = reclamation.qualificationDetails.find(detail => detail.code === ReclamationQualificationDetailCodes.Depot);
        if (detailDepot !== undefined) {
            depot = detailDepot.valeurObject;
        }

        return depot;
    }

    getDepotNom() {
        const { reclamation } = this.props;

        let depot = '';

        const detailDepot = reclamation.qualificationDetails.find(detail => detail.code === ReclamationQualificationDetailCodes.Depot);
        if (detailDepot !== undefined) {
            depot = `${detailDepot.valeurString} - ${detailDepot.valeurObject.nom}`;
        }

        return depot;
    }

    getMagasinRetourATC() {
        const { reclamation } = this.props;

        let magasinRetourATC = '';

        const detailMagasinRetourATC = reclamation.qualificationDetails.find(detail => detail.code === ReclamationQualificationDetailCodes.MagasinRetourATC);
        if (detailMagasinRetourATC !== undefined) {
            magasinRetourATC = detailMagasinRetourATC.valeurString;
        }

        return magasinRetourATC;
    }

    getPlateformeRetour() {
        const { reclamation } = this.props;

        let plateformeRetour = '';

        const detail = reclamation.qualificationDetails.find(d => d.code === ReclamationQualificationDetailCodes.PlateformeRetour);
        if (detail !== undefined) {
            plateformeRetour = detail.valeurString;
        }

        return plateformeRetour;
    }

    getModePriseEnCharge() {
        const { reclamation } = this.props;

        let modePriseEnCharge = '';

        const detailModePriseEnCharge = reclamation.qualificationDetails.find(detail => detail.code === ReclamationQualificationDetailCodes.ModePriseEnCharge);
        if (detailModePriseEnCharge !== undefined) {
            modePriseEnCharge = detailModePriseEnCharge.valeurInteger;
        }

        return modePriseEnCharge;
    }

    getModePriseEnChargeATC() {
        const { reclamation } = this.props;

        let modePriseEnChargeATC = '';

        const detailModePriseEnChargeATC = reclamation.qualificationDetails.find(detail => detail.code === ReclamationQualificationDetailCodes.ModePriseEnChargeATC);
        if (detailModePriseEnChargeATC !== undefined) {
            modePriseEnChargeATC = detailModePriseEnChargeATC.valeurInteger;
        }

        return modePriseEnChargeATC;
    }

    getClientInforme() {
        const { reclamation } = this.props;

        let clientInforme = false;

        const detailClientInforme = reclamation.qualificationDetails.find(detail => detail.code === ReclamationQualificationDetailCodes.ClientInforme);
        if (detailClientInforme !== undefined) {
            clientInforme = detailClientInforme.valeurBool;
        }

        return clientInforme;
    }

    getEmails() {
        const { reclamation } = this.props;

        let emails = '';

        const detailEmails = reclamation.qualificationDetails.find(detail => detail.code === ReclamationQualificationDetailCodes.Emails);
        if (detailEmails !== undefined) {
            emails = detailEmails.valeurString;
        }

        return emails;
    }

    getFraisRetour = (modeRetour, modePriseEnCharge, depot, declaratifRebut) => {
        const { onAddRetour } = this.props;
        const { montant, decote, decoteJours, exclusionFrais, fraisRetour, autoriseJoker, pctAvoir12Mois, exclusionDecote } = this.state;

        let fdp = fraisRetour;
        let messageFDP = '';
        let _autoriseJoker = autoriseJoker;
        let _pctAvoir12Mois = pctAvoir12Mois;


        if (modeRetour.toString() === '2' && modePriseEnCharge.toString() === '0') {
            messageFDP = 'Pas de frais de retour car le client s\'occupe lui-même du transport';
            fdp = 0;
            _autoriseJoker = false;
            _pctAvoir12Mois = 0;
        }
        else {

            // On détermine le message à afficher ici.
            switch (exclusionFrais) {
                case 1:
                    messageFDP = 'Le client est sujet à la gratuité des frais de retour.';
                    break;
                case 2:
                    messageFDP = 'La typologie du client est sujette à la gratuité des frais de retour.';
                    break;
                case 3:
                    messageFDP = 'Des infos liées à la pièce impliquent la gratuité des frais de retour.';
                    break;
                case 4:
                    messageFDP = 'Tous les articles à retourner appartiennent à une famille exclue des frais de retour.';
                    break;
                default:
                    break;
            }

            // Test de l'exclusion des frais au niveau magasin
            if (exclusionFrais === 0 && depot) {
                if (depot.fraisRetourExclusion) {
                    messageFDP = 'Le magasin est exclu des frais de retour.';
                    fdp = 0;
                    _autoriseJoker = false;
                }
            }
        }


        const montantRebut = declaratifRebut.reduce((previous, current) => previous + Number(current.quantiteRebut * current.prixUnitaire), 0);

        // On met à jour le noeud retour sur la reclamation.
        onAddRetour({ montant, decote, decoteJours, fdp, messageFDP, autoriseJoker: _autoriseJoker, pctAvoir12Mois: _pctAvoir12Mois, exclusionDecote, montantRebut });

    }

    getArticlesEnEtatRevente() {
        const { reclamation } = this.props;

        let value = null;

        const articlesEnEtatRevente = reclamation.qualificationDetails.find(detail => detail.code === ReclamationQualificationDetailCodes.Rebut);
        if (articlesEnEtatRevente !== undefined) {
            value = articlesEnEtatRevente.valeurBool;
        }

        return value;
    }

    getRemboursementType() {
        const { reclamation } = this.props;

        let type = null;

        const detailType = reclamation.qualificationDetails.find(detail => detail.code === ReclamationQualificationDetailCodes.TypeRemboursement);
        if (detailType !== undefined) {
            type = detailType.valeurInteger;
        }

        return type;
    }

    getRepriseMarchandise() {
        const { reclamation } = this.props;

        let reprise = null;

        const detailReprise = reclamation.qualificationDetails.find(detail => detail.code === ReclamationQualificationDetailCodes.RepriseMarchandise);
        if (detailReprise !== undefined) {
            reprise = detailReprise.valeurInteger;
        }

        return reprise;
    }



    checkErrors(newState, modeRetour, codeDepot, clientInforme, modePriseEnCharge, modePriseEnChargeATC, magasinRetourATC, emails,
        plateformeRetour, articlesEnEtatRevente, typeRemboursement, repriseMarchandise) {

        const { reclamation, utilisateurMagasin } = this.props;

        let currentState = newState;

        if (!currentState) {
            currentState = { ...this.state }
        }

        let errors = [
            { key: ReclamationQualificationDetailCodes.Piece, errorMsg: null },
            { key: ReclamationQualificationDetailCodes.NbLignes, errorMsg: null },
            { key: "Etape.ChoixDesLignes", errorMsg: null },
            { key: "Etape.EligibiliteResult_Eligibilite.OK", errorMsg: null },
            { key: "Etape.EligibiliteResult_Eligibilite.NonMontantFaible_clientInforme", errorMsg: null },
            { key: "Etape.EligibiliteResult_Eligibilite.NonQuantiteRetournableDepassee", errorMsg: null },
            { key: ReclamationQualificationDetailCodes.Depot, errorMsg: null },
            { key: ReclamationQualificationDetailCodes.MagasinRetourATC, errorMsg: null },
            { key: ReclamationQualificationDetailCodes.ModePriseEnCharge, errorMsg: null },
            { key: ReclamationQualificationDetailCodes.ModePriseEnChargeATC, errorMsg: null },
            { key: ReclamationQualificationDetailCodes.ClientInforme, errorMsg: null },
            { key: ReclamationQualificationDetailCodes.ModeRetour, errorMsg: null },
            { key: ReclamationQualificationDetailCodes.Rebut, errorMsg: null },
            { key: ReclamationQualificationDetailCodes.PlateformeRetour, errorMsg: null },
            { key: ReclamationQualificationDetailCodes.AdresseEnlevement, errorMsg: null },
            { key: ReclamationQualificationDetailCodes.AdresseDestination, errorMsg: null },
            { key: ReclamationQualificationDetailCodes.ContactClient, errorMsg: null },
            { key: ReclamationQualificationDetailCodes.Poids, errorMsg: null },
            { key: ReclamationQualificationDetailCodes.NombreColis, errorMsg: null },
            { key: ReclamationQualificationDetailCodes.Emails, errorMsg: null },
            { key: ReclamationQualificationDetailCodes.TypeRemboursement, errorMsg: null },
            { key: ReclamationQualificationDetailCodes.RepriseMarchandise, errorMsg: null }
        ];

        if (reclamation.numeroPiece.length === 0) {
            let index = errors.findIndex(x => x.key === ReclamationQualificationDetailCodes.Piece);
            errors[index].errorMsg = 'Veuillez sélectionner une pièce.';
        }

        if (reclamation.lignes.length === 0) {
            let index = errors.findIndex(x => x.key === ReclamationQualificationDetailCodes.NbLignes);
            errors[index].errorMsg = 'Au moins une ligne doit être renseignée.';
        }

        if (currentState.etape === Etape.ChoixDesLignes) {
            let index = errors.findIndex(x => x.key === "Etape.ChoixDesLignes");
            errors[index].errorMsg = 'Veuillez vérifier l\'éligibilité du retour.';
        }

        if (currentState.etape === Etape.EligibiliteResult && (currentState.eligibilite === EligibiliteRetourVolontaire.OK || currentState.eligibilite === EligibiliteRetourVolontaire.OKSeulementMagasin)) {
            let index = errors.findIndex(x => x.key === "Etape.EligibiliteResult_Eligibilite.OK");
            errors[index].errorMsg = 'Veuillez remplir le formulaire de retour';
        }

        if (currentState.etape === Etape.EligibiliteResult && currentState.eligibilite === EligibiliteRetourVolontaire.NonMontantFaible && !clientInforme) {
            let index = errors.findIndex(x => x.key === "Etape.EligibiliteResult_Eligibilite.NonMontantFaible_clientInforme");
            errors[index].errorMsg = clientInformeEligibiliteNonLabel;
        }

        // DEV-4012 : quantité à retourner > quantité retournable → création bloquée (l'utilisateur doit corriger).
        if (currentState.etape === Etape.EligibiliteResult && currentState.eligibilite === EligibiliteRetourVolontaire.NonQuantiteRetournableDepassee) {
            let index = errors.findIndex(x => x.key === "Etape.EligibiliteResult_Eligibilite.NonQuantiteRetournableDepassee");
            errors[index].errorMsg = eligibiliteNonQuantiteRetournableDepasseeLabel;
        }

        if (currentState.etape === Etape.TraitementRetour) {
            if (modeRetour === '') {
                let index = errors.findIndex(x => x.key === ReclamationQualificationDetailCodes.ModeRetour);
                errors[index].errorMsg = modeRetourLabel;
            }
            else {

                if (modeRetour === 1) {

                    if (codeDepot === '') {
                        let index = errors.findIndex(x => x.key === ReclamationQualificationDetailCodes.Depot);
                        errors[index].errorMsg = 'Veuillez choisir un magasin.';
                    }

                    if (utilisateurMagasin) {
                        if (articlesEnEtatRevente === null) {
                            let index = errors.findIndex(x => x.key === ReclamationQualificationDetailCodes.Rebut);
                            errors[index].errorMsg = 'Veuillez sélectionner si les articles sont en état de revente.';

                        } else if (!articlesEnEtatRevente && !currentState.rebutValider) {
                            let index = errors.findIndex(x => x.key === ReclamationQualificationDetailCodes.Rebut);
                            errors[index].errorMsg = 'Veuillez sélectionner et valider des articles en rebut.';
                        }
                    }
                }
                else if (modeRetour === 2) {

                    if (modePriseEnCharge === '') {
                        let index = errors.findIndex(x => x.key === ReclamationQualificationDetailCodes.ModePriseEnCharge);
                        errors[index].errorMsg = modePriseEnChargeLabel;
                    }
                    else if (modePriseEnCharge === 0) {

                        if (plateformeRetour === '') {
                            let index = errors.findIndex(x => x.key === ReclamationQualificationDetailCodes.PlateformeRetour);
                            errors[index].errorMsg = 'Veuillez sélectionner une plateforme.';
                        }
                    }
                }
                else if (modeRetour === 3) {
                    if (modePriseEnChargeATC === '') {
                        let index = errors.findIndex(x => x.key === ReclamationQualificationDetailCodes.ModePriseEnChargeATC);
                        errors[index].errorMsg = modePriseEnChargeATCLabel;
                    }
                    else {

                        if (modePriseEnChargeATC === 0) {
                            // Le magasin est obligatoire
                            if (magasinRetourATC === '') {
                                let index = errors.findIndex(x => x.key === ReclamationQualificationDetailCodes.MagasinRetourATC);
                                errors[index].errorMsg = 'Veuillez spécifier le magasin de retour.';
                            }
                        }
                    }
                }

                if (modeRetour === 0 || (modeRetour === 2 && modePriseEnCharge === 1)) {

                    if (stringHelper.isNullOrWhitespace(currentState.adresseEnlevementNom)
                        || stringHelper.isNullOrWhitespace(currentState.adresseEnlevementCodePostal)
                        || stringHelper.isNullOrWhitespace(currentState.adresseEnlevementVille)
                        || (stringHelper.isNullOrWhitespace(currentState.adresseEnlevementAdresse1) && stringHelper.isNullOrWhitespace(currentState.adresseEnlevementAdresse2))
                    ) {
                        let index = errors.findIndex(x => x.key === ReclamationQualificationDetailCodes.AdresseEnlevement);
                        errors[index].errorMsg = 'Il manque des informations sur l\'adresse d\'enlèvement.';
                    }

                    if (currentState.adresseDestinationCode === 3) {

                        if (stringHelper.isNullOrWhitespace(currentState.adresseDestinationNom)
                            || stringHelper.isNullOrWhitespace(currentState.adresseDestinationCodePostal)
                            || stringHelper.isNullOrWhitespace(currentState.adresseDestinationVille)
                            || (stringHelper.isNullOrWhitespace(currentState.adresseDestinationAdresse1) && stringHelper.isNullOrWhitespace(currentState.adresseDestinationAdresse2))
                        ) {
                            let index = errors.findIndex(x => x.key === ReclamationQualificationDetailCodes.AdresseDestination);
                            errors[index].errorMsg = 'Il manque des informations sur l\'adresse de destination.';
                        }
                    }
                    else {
                        if (currentState.adresseDestinationCode === '') {
                            let index = errors.findIndex(x => x.key === ReclamationQualificationDetailCodes.AdresseDestination);
                            errors[index].errorMsg = 'Adresse de destination manquante.';
                        }
                    }

                    if (stringHelper.isNullOrWhitespace(currentState.donneurOrdreNom)
                        || stringHelper.isNullOrWhitespace(currentState.donneurOrdreMail)
                        || stringHelper.isNullOrWhitespace(currentState.donneurOrdreTelephone)
                    ) {
                        let index = errors.findIndex(x => x.key === ReclamationQualificationDetailCodes.ContactClient);
                        errors[index].errorMsg = 'Il manque des informations sur le contact client.';
                    }

                    // Prévoir un controle sur la date d'enlèvement ???
                    //errors.push({ key: ReclamationQualificationDetailCodes.DateEnlevement, errorMsg: null });

                    if (!currentState[ReclamationQualificationDetailCodes.Poids]) {
                        let index = errors.findIndex(x => x.key === ReclamationQualificationDetailCodes.Poids);
                        errors[index].errorMsg = 'Veuillez spécifier le poids théorique du retour.';
                    }

                    if (currentState[ReclamationQualificationDetailCodes.NombrePalettes] === 0
                        && currentState[ReclamationQualificationDetailCodes.NombreColis] === 0
                        && currentState[ReclamationQualificationDetailCodes.NombreLongueurs] === 0) {

                        let index = errors.findIndex(x => x.key === ReclamationQualificationDetailCodes.NombreColis);
                        errors[index].errorMsg = 'Veuillez spécifier le colisage.';
                    }
                }

                if (!clientInforme) {
                    let index = errors.findIndex(x => x.key === ReclamationQualificationDetailCodes.ClientInforme);
                    errors[index].errorMsg = clientInformeLabel;
                }

                if (!emails || emails === '') {
                    let index = errors.findIndex(x => x.key === ReclamationQualificationDetailCodes.Emails);
                    errors[index].errorMsg = 'Veuillez spécifier un mail pour la notification.';
                }

                if (modeRetour === 1 && utilisateurMagasin) {

                    //console.log('checkErrors', typeRemboursement, repriseMarchandise);

                    if (typeRemboursement === null) {
                        let index = errors.findIndex(x => x.key === ReclamationQualificationDetailCodes.TypeRemboursement);
                        errors[index].errorMsg = 'Veuillez sélectionner un type de remboursement.';
                    }

                    if (repriseMarchandise === null) {
                        let index = errors.findIndex(x => x.key === ReclamationQualificationDetailCodes.RepriseMarchandise);
                        errors[index].errorMsg = 'Veuillez sélectionner un type de reprise marchandise.';
                    }
                }
            }
        }

        return errors;
    }



    handleChangePanel = panel => (event, expandedPanel) => {
        this.setState({
            expandedPanel: expandedPanel ? panel : false
        });
    }

    handleChange = (event) => {
        const { onChange } = this.props;

        let newState = { ...this.state, [event.target.name]: event.target.value };

        this.setState(newState);

        const errors = this.checkErrors(
            newState,
            this.getModeRetour(),
            this.getCodeDepot(),
            this.getClientInforme(),
            this.getModePriseEnCharge(),
            this.getModePriseEnChargeATC(),
            this.getMagasinRetourATC(),
            this.getEmails(),
            this.getPlateformeRetour(),
            this.getArticlesEnEtatRevente(),
            this.getRemboursementType(),
            this.getRepriseMarchandise()
        );

        onChange(event, errors);
    }

    handleChangeInteger = (event) => {
        const { onDetailChange } = this.props;

        let newState;

        newState = {
            ...this.state,
            [event.target.name]: Math.max(event.target.value, 0)
        };

        this.setState(newState);

        const qualificationDetail = {
            position: parseInt(event.target.attributes['position'].nodeValue, 10),
            code: event.target.name,
            type: ReclamationQualificationDetailType.Nombre,
            libelle: qualificationDetailLabels[event.target.name],
            valeurInteger: parseInt(event.target.value, 10)

        };

        const errors = this.checkErrors(
            newState,
            this.getModeRetour(),
            this.getCodeDepot(),
            this.getClientInforme(),
            this.getModePriseEnCharge(),
            this.getModePriseEnChargeATC(),
            this.getMagasinRetourATC(),
            this.getEmails(),
            this.getPlateformeRetour(),
            this.getArticlesEnEtatRevente(),
            this.getRemboursementType(),
            this.getRepriseMarchandise()
        );

        onDetailChange(qualificationDetail, errors, null);
    }

    handleChangeDate = (event) => {
        const { onDetailChange } = this.props;

        let newState;

        newState = {
            ...this.state,
            [event.target.name]: event.target.value
        };

        this.setState(newState);

        const qualificationDetails = [];
        const detailsToDelete = [];

        if (event.target.value) {
            qualificationDetails.push({
                position: 5,
                code: event.target.name,
                type: ReclamationQualificationDetailType.Date,
                libelle: qualificationDetailLabels[event.target.name],
                valeurDate: event.target.value.toDateString()

            });
        }
        else {
            detailsToDelete.push(event.target.name);
        }

        const errors = this.checkErrors(
            newState,
            this.getModeRetour(),
            this.getCodeDepot(),
            this.getClientInforme(),
            this.getModePriseEnCharge(),
            this.getModePriseEnChargeATC(),
            this.getMagasinRetourATC(),
            this.getEmails(),
            this.getPlateformeRetour(),
            this.getArticlesEnEtatRevente(),
            this.getRemboursementType(),
            this.getRepriseMarchandise()
        );

        onDetailChange(qualificationDetails, errors, detailsToDelete);
    }

    handleSelectPiece = (value, piece) => {
        const { onSelectPiece, utilisateurMagasin } = this.props;

        // DEV-4012 : le clic sur un BL ne charge plus les lignes (withLignes={false} sur la recherche).
        // Le choix du type de retour (BL/Commande) est proposé dès que la pièce a une commande
        // exploitable (hors profil magasin). Sinon, on court-circuite vers le BL : chargement direct
        // des lignes du BL, sans écran de choix.
        const proposerChoixTypeRetour = Boolean(piece && piece.commandeGestionCle) && !utilisateurMagasin;

        if (!proposerChoixTypeRetour) {
            // Pas de choix à faire : on force le BL et on enchaîne sur ses lignes.
            this.handleSelectTypeRetour(TypePiece.BonLivraison, piece || null);
            return;
        }

        // On mémorise la pièce et on ouvre l'accordéon « Type de retour ». Le chargement des lignes
        // (BL ou commande) ne se fait qu'après le choix explicite (handleSelectTypeRetour).
        this.setState({
            expandedPanel: 'panelTypeRetour',
            etape: Etape.ChoixDeLaPiece,
            typeRetour: null,
            pieceSelectionnee: piece || null
        });

        const errors = this.checkErrors(
            null,
            this.getModeRetour(),
            this.getCodeDepot(),
            this.getClientInforme(),
            this.getModePriseEnCharge(),
            this.getModePriseEnChargeATC(),
            this.getMagasinRetourATC(),
            this.getEmails(),
            this.getPlateformeRetour(),
            this.getArticlesEnEtatRevente(),
            this.getRemboursementType(),
            this.getRepriseMarchandise()
        );

        onSelectPiece(value, errors);
    }

    // DEV-4012 : choix du type de retour (BL ou commande). Déclenche le chargement des lignes
    // de la source choisie dans le store partagé pieceGestionLigne, puis ouvre l'accordéon des lignes.
    // pieceOverride permet d'enchaîner depuis handleSelectPiece sans attendre le setState de pieceSelectionnee.
    handleSelectTypeRetour = (typeRetour, pieceOverride = null) => {
        const { onSelectPiece, fetchPieceGestionLignes, fetchCommandeGestionLignes } = this.props;
        const pieceSelectionnee = pieceOverride || this.state.pieceSelectionnee;

        if (!pieceSelectionnee) {
            return;
        }

        // Mêmes options que la recherche de pièce du RV (cf. renderPieceGestionSearch).
        const defautProduitInfos = false;
        const logistiqueInfos = true;
        const savInfos = false;
        const codeDepot = pieceSelectionnee.codeDepot || null;

        const errors = this.checkErrors(
            null,
            this.getModeRetour(),
            this.getCodeDepot(),
            this.getClientInforme(),
            this.getModePriseEnCharge(),
            this.getModePriseEnChargeATC(),
            this.getMagasinRetourATC(),
            this.getEmails(),
            this.getPlateformeRetour(),
            this.getArticlesEnEtatRevente(),
            this.getRemboursementType(),
            this.getRepriseMarchandise()
        );

        // Propage le choix au form container : pose typePiece/pieceCle selon BL ou commande,
        // vide les lignes déjà saisies (numéros de ligne différents BL/commande) et l'erreur NbLignes.
        onSelectPiece(pieceSelectionnee.numeroPiece, errors, true, typeRetour);

        if (typeRetour === TypePiece.Commande) {
            fetchCommandeGestionLignes(pieceSelectionnee.commandeGestionCle, codeDepot);
        } else {
            const cleBl = pieceSelectionnee.bonLivraisonGestionCle || pieceSelectionnee.numeroPiece;
            fetchPieceGestionLignes(cleBl, defautProduitInfos, logistiqueInfos, savInfos, codeDepot);
        }

        this.setState({
            typeRetour: typeRetour,
            pieceSelectionnee: pieceSelectionnee,
            expandedPanel: 'panelLignes',
            etape: Etape.ChoixDesLignes
        });
    }

    handleAddLigne = (ligne) => {
        const { onAddLigne } = this.props;

        const errors = this.checkErrors(
            null,
            this.getModeRetour(),
            this.getCodeDepot(),
            this.getClientInforme(),
            this.getModePriseEnCharge(),
            this.getModePriseEnChargeATC(),
            this.getMagasinRetourATC(),
            this.getEmails(),
            this.getPlateformeRetour(),
            this.getArticlesEnEtatRevente(),
            this.getRemboursementType(),
            this.getRepriseMarchandise()
        );

        onAddLigne(ligne, errors);
    }

    handleRemoveLigne = (numeroLigne) => {
        const { onRemoveLigne } = this.props;

        const errors = this.checkErrors(
            null,
            this.getModeRetour(),
            this.getCodeDepot(),
            this.getClientInforme(),
            this.getModePriseEnCharge(),
            this.getModePriseEnChargeATC(),
            this.getMagasinRetourATC(),
            this.getEmails(),
            this.getPlateformeRetour(),
            this.getArticlesEnEtatRevente(),
            this.getRemboursementType(),
            this.getRepriseMarchandise()
        );

        onRemoveLigne(numeroLigne, errors);
    }

    handleSelectAllLignes = () => {
        const { onSelectAllLignes } = this.props;

        const errors = this.checkErrors(
            null,
            this.getModeRetour(),
            this.getCodeDepot(),
            this.getClientInforme(),
            this.getModePriseEnCharge(),
            this.getModePriseEnChargeATC(),
            this.getMagasinRetourATC(),
            this.getEmails(),
            this.getPlateformeRetour(),
            this.getArticlesEnEtatRevente(),
            this.getRemboursementType(),
            this.getRepriseMarchandise()
        );

        onSelectAllLignes({ errors, typeAnomalie: AnomalieType.DemandeRetourVolontaire, commentaire: "" });

    }

    handleClickCheckEligibilite = async () => {
        const { onClickCheckEligibilite, onRemoveRetour, onAddRetour, onCheckErrors } = this.props;

        this.setState({ controleEligibiliteEnCours: true });

        var result = await onClickCheckEligibilite();


        let newState = {
            //...this.state,
            eligibilite: result.eligibilite,
            seulementMagasinFlags: result.seulementMagasinFlags,
            etape: Etape.EligibiliteResult,
            montant: result.montant,
            eligibiliteNonMontantFaibleDialogOpen: (result.eligibilite === EligibiliteRetourVolontaire.NonMontantFaible),
            eligibiliteOkSeulementMagasinDialogOpen: (result.eligibilite === EligibiliteRetourVolontaire.OKSeulementMagasin),
            decote: result.decote,
            decoteJours: result.decoteJours,
            exclusionDecote: result.exclusionDecote,
            fraisRetour: result.fraisRetour,
            exclusionFrais: result.exclusionFrais,
            autoriseJoker: result.autoriseJoker,
            pctAvoir12Mois: result.pctAvoir12Mois,
            controleEligibiliteEnCours: false
        };

        if (result.eligibilite !== EligibiliteRetourVolontaire.OK && result.eligibilite !== EligibiliteRetourVolontaire.OKSeulementMagasin) {
            // On appelle une méthode qui supprime le noeud retour sur la réclamation si il existe.
            onRemoveRetour();

        } else {
            // On ajoute les infos de décôte
            onAddRetour({
                montant: result.montant,
                decote: result.decote,
                decoteJours: result.decoteJours,
                fdp: result.fraisRetour,
                messageFDP: "",
                autoriseJoker: result.autoriseJoker,
                pctAvoir12Mois: result.pctAvoir12Mois,
                exclusionDecote: result.exclusionDecote
            });
        }

        this.setState(newState);

        const errors = this.checkErrors(
            newState,
            this.getModeRetour(),
            this.getCodeDepot(),
            this.getClientInforme(),
            this.getModePriseEnCharge(),
            this.getModePriseEnChargeATC(),
            this.getMagasinRetourATC(),
            this.getEmails(),
            this.getPlateformeRetour(),
            this.getArticlesEnEtatRevente(),
            this.getRemboursementType(),
            this.getRepriseMarchandise()
        );

        onCheckErrors(errors);
    }

    handleClickEditLignes = () => {
        const { onCheckErrors } = this.props;

        let newState = { ...this.state, eligibilite: EligibiliteRetourVolontaire.OK, etape: Etape.ChoixDesLignes };

        this.setState(newState);

        const errors = this.checkErrors(
            newState,
            this.getModeRetour(),
            this.getCodeDepot(),
            this.getClientInforme(),
            this.getModePriseEnCharge(),
            this.getModePriseEnChargeATC(),
            this.getMagasinRetourATC(),
            this.getEmails(),
            this.getPlateformeRetour(),
            this.getArticlesEnEtatRevente(),
            this.getRemboursementType(),
            this.getRepriseMarchandise()
        );

        onCheckErrors(errors);
    }

    handleClickTreatReturn = () => {

        let newState = { ...this.state, etape: Etape.TraitementRetour, expandedPanel: 'panelTraitementRetour' };

        this.setState(newState);
    }

    handleQualificationDetailsModeRetourChange = (event) => {

        const { onDetailChange, reclamation } = this.props;

        let depot = null;
        let codeDepot = null;

        const qualificationDetails = [{
            position: 1,
            code: ReclamationQualificationDetailCodes.ModeRetour,
            type: ReclamationQualificationDetailType.ChoixSimple,
            libelle: modeRetourLabel,
            valeurString: modeRetourLabels[event.target.value],
            valeurInteger: parseInt(event.target.value, 10)
        }];

        let detailsToDelete = [];

        if (event.target.value === '0') {
            detailsToDelete.push(ReclamationQualificationDetailCodes.Depot);
            detailsToDelete.push(ReclamationQualificationDetailCodes.ModePriseEnCharge);
            detailsToDelete.push(ReclamationQualificationDetailCodes.PlateformeRetour);
            detailsToDelete.push(ReclamationQualificationDetailCodes.AdresseRetour);
            detailsToDelete.push(ReclamationQualificationDetailCodes.MagasinRetourATC);
            detailsToDelete.push(ReclamationQualificationDetailCodes.ModePriseEnChargeATC);

            qualificationDetails.push(
                {
                    position: 2,
                    code: ReclamationQualificationDetailCodes.AdresseEnlevement,
                    type: ReclamationQualificationDetailType.Texte,
                    libelle: "Adresse d'enlèvement",
                    valeurString: this.adresseEnlevementToStr(this.state)
                });
            qualificationDetails.push(
                {
                    position: 3,
                    code: ReclamationQualificationDetailCodes.AdresseDestination,
                    type: ReclamationQualificationDetailType.Texte,
                    libelle: "Adresse de destination",
                    valeurString: this.adresseDestinationToStr(this.state),
                    valeurInteger: this.state.adresseDestinationCode
                });
            qualificationDetails.push(
                {
                    position: 4,
                    code: ReclamationQualificationDetailCodes.ContactClientNom,
                    type: ReclamationQualificationDetailType.Texte,
                    libelle: "Contact, nom",
                    valeurString: this.state.donneurOrdreNom
                });
            qualificationDetails.push(
                {
                    position: 4,
                    code: ReclamationQualificationDetailCodes.ContactClientEmail,
                    type: ReclamationQualificationDetailType.Texte,
                    libelle: "Contact, email",
                    valeurString: this.state.donneurOrdreMail
                });
            qualificationDetails.push(
                {
                    position: 4,
                    code: ReclamationQualificationDetailCodes.ContactClientTelephone,
                    type: ReclamationQualificationDetailType.Texte,
                    libelle: "Contact, téléphone",
                    valeurString: this.state.donneurOrdreTelephone
                });



            if (this.state[ReclamationQualificationDetailCodes.DateEnlevement] !== null) {
                qualificationDetails.push(
                    {
                        position: 5,
                        code: ReclamationQualificationDetailCodes.DateEnlevement,
                        type: ReclamationQualificationDetailType.Date,
                        libelle: qualificationDetailLabels[ReclamationQualificationDetailCodes.DateEnlevement],
                        valeurDate: this.state[ReclamationQualificationDetailCodes.DateEnlevement].toDateString()
                    });
            }
            qualificationDetails.push(
                {
                    position: 6,
                    code: ReclamationQualificationDetailCodes.Poids,
                    type: ReclamationQualificationDetailType.Nombre,
                    libelle: qualificationDetailLabels[ReclamationQualificationDetailCodes.Poids],
                    valeurInteger: this.state[ReclamationQualificationDetailCodes.Poids]
                });
            qualificationDetails.push(
                {
                    position: 7,
                    code: ReclamationQualificationDetailCodes.NombreColis,
                    type: ReclamationQualificationDetailType.Nombre,
                    libelle: qualificationDetailLabels[ReclamationQualificationDetailCodes.NombreColis],
                    valeurInteger: this.state[ReclamationQualificationDetailCodes.NombreColis]
                });
            qualificationDetails.push(
                {
                    position: 8,
                    code: ReclamationQualificationDetailCodes.NombrePalettes,
                    type: ReclamationQualificationDetailType.Nombre,
                    libelle: qualificationDetailLabels[ReclamationQualificationDetailCodes.NombrePalettes],
                    valeurInteger: this.state[ReclamationQualificationDetailCodes.NombrePalettes]
                });
            qualificationDetails.push(
                {
                    position: 9,
                    code: ReclamationQualificationDetailCodes.NombreLongueurs,
                    type: ReclamationQualificationDetailType.Nombre,
                    libelle: qualificationDetailLabels[ReclamationQualificationDetailCodes.NombreLongueurs],
                    valeurInteger: this.state[ReclamationQualificationDetailCodes.NombreLongueurs]
                });


            qualificationDetails.push(
                {
                    position: 10,
                    code: ReclamationQualificationDetailCodes.TransporteurInitial,
                    type: ReclamationQualificationDetailType.Texte,
                    libelle: "Transporteur initial",
                    valeurString: this.props.transporteurInitial ? this.props.transporteurInitial.nom + ' / ' + this.props.transporteurInitial.contrat : ''
                });

        }
        else if (event.target.value === '1') {

            depot = reclamation.magasin ? reclamation.magasin : null;
            codeDepot = reclamation.magasin ? reclamation.magasin.codeDepot : '';
            if (codeDepot === '') {
                let utilisateur = JSON.parse(localStorage.getItem('utilisateur'));

                if (sansDepotMagasin(utilisateur)) {
                    if (utilisateur.depots && utilisateur.depots.length === 1) {
                        codeDepot = utilisateur.depots[0].code;
                    }
                }
                else {
                    codeDepot = utilisateur.depot.code;
                }
            }

            if (codeDepot !== '') {
                qualificationDetails.push(
                    {
                        position: 1,
                        code: ReclamationQualificationDetailCodes.Depot,
                        type: ReclamationQualificationDetailType.CodeDepot,
                        libelle: "Magasin",
                        valeurString: codeDepot
                    });
            }


            detailsToDelete.push(ReclamationQualificationDetailCodes.ModePriseEnCharge);
            detailsToDelete.push(ReclamationQualificationDetailCodes.PlateformeRetour);
            detailsToDelete.push(ReclamationQualificationDetailCodes.AdresseRetour);
            detailsToDelete.push(ReclamationQualificationDetailCodes.MagasinRetourATC);
            detailsToDelete.push(ReclamationQualificationDetailCodes.ModePriseEnChargeATC);
            detailsToDelete.push(ReclamationQualificationDetailCodes.TransporteurInitial);

        }
        else if (event.target.value === '2') {
            detailsToDelete.push(ReclamationQualificationDetailCodes.Depot);
            detailsToDelete.push(ReclamationQualificationDetailCodes.ModePriseEnCharge);
            detailsToDelete.push(ReclamationQualificationDetailCodes.PlateformeRetour);
            detailsToDelete.push(ReclamationQualificationDetailCodes.AdresseRetour);
            detailsToDelete.push(ReclamationQualificationDetailCodes.MagasinRetourATC);
            detailsToDelete.push(ReclamationQualificationDetailCodes.ModePriseEnChargeATC);
            detailsToDelete.push(ReclamationQualificationDetailCodes.TransporteurInitial);
        }
        else if (event.target.value === '3') {
            detailsToDelete.push(ReclamationQualificationDetailCodes.Depot);
            detailsToDelete.push(ReclamationQualificationDetailCodes.ModePriseEnCharge);
            detailsToDelete.push(ReclamationQualificationDetailCodes.PlateformeRetour);
            detailsToDelete.push(ReclamationQualificationDetailCodes.AdresseRetour);
            detailsToDelete.push(ReclamationQualificationDetailCodes.MagasinRetourATC);
            detailsToDelete.push(ReclamationQualificationDetailCodes.ModePriseEnChargeATC);
            detailsToDelete.push(ReclamationQualificationDetailCodes.TransporteurInitial);
        }

        if (event.target.value === '1' || event.target.value === '2' || event.target.value === '3') {

            detailsToDelete.push(ReclamationQualificationDetailCodes.AdresseEnlevement);

            detailsToDelete.push(ReclamationQualificationDetailCodes.AdresseDestination);
            detailsToDelete.push(ReclamationQualificationDetailCodes.ContactClient);
            detailsToDelete.push(ReclamationQualificationDetailCodes.DateEnlevement);
            detailsToDelete.push(ReclamationQualificationDetailCodes.NombreColis);
            detailsToDelete.push(ReclamationQualificationDetailCodes.NombrePalettes);
            detailsToDelete.push(ReclamationQualificationDetailCodes.NombreLongueurs);
        }

        const value = event.target.value;

        //console.log("choix du mode de retour", depot);

        // On recalcul les frais de retour.
        this.getFraisRetour(value, this.getModePriseEnCharge(), depot, this.state.declaratifRebut);

        const errors = this.checkErrors(
            null,
            parseInt(value, 10),
            codeDepot,
            this.getClientInforme(),
            this.getModePriseEnCharge(),
            this.getModePriseEnChargeATC(),
            this.getMagasinRetourATC(),
            this.getEmails(),
            this.getPlateformeRetour(),
            this.getArticlesEnEtatRevente(),
            this.getRemboursementType(),
            this.getRepriseMarchandise()
        );

        onDetailChange(qualificationDetails, errors, detailsToDelete);

    }

    handleChangeAdresseEnlevement = (event) => {
        const { onDetailChange } = this.props;

        let newState = { ...this.state, [event.target.name]: event.target.value };

        this.setState(newState);

        const qualificationDetail = {
            position: 2,
            code: ReclamationQualificationDetailCodes.AdresseEnlevement,
            type: ReclamationQualificationDetailType.Texte,
            libelle: "Adresse d'enlèvement",
            valeurString: this.adresseEnlevementToStr(newState)
        };

        const errors = this.checkErrors(
            newState,
            this.getModeRetour(),
            this.getCodeDepot(),
            this.getClientInforme(),
            this.getModePriseEnCharge(),
            this.getModePriseEnChargeATC(),
            this.getMagasinRetourATC(),
            this.getEmails(),
            this.getPlateformeRetour(),
            this.getArticlesEnEtatRevente(),
            this.getRemboursementType(),
            this.getRepriseMarchandise()
        );

        onDetailChange(qualificationDetail, errors, null);
    }

    handleChangeAdresseDestination = (event) => {
        const { onDetailChange } = this.props;

        let newState = { ...this.state, [event.target.name]: event.target.value };

        if (event.target.name === "adresseDestinationCode") {
            newState = { ...this.state, [event.target.name]: parseInt(event.target.value, 10) };
        }

        this.setState(newState);

        const qualificationDetail = {
            position: 3,
            code: ReclamationQualificationDetailCodes.AdresseDestination,
            type: ReclamationQualificationDetailType.Texte,
            libelle: "Adresse de destination",
            valeurString: this.adresseDestinationToStr(newState),
            valeurInteger: newState.adresseDestinationCode

        };

        const errors = this.checkErrors(
            newState,
            this.getModeRetour(),
            this.getCodeDepot(),
            this.getClientInforme(),
            this.getModePriseEnCharge(),
            this.getModePriseEnChargeATC(),
            this.getMagasinRetourATC(),
            this.getEmails(),
            this.getPlateformeRetour(),
            this.getArticlesEnEtatRevente(),
            this.getRemboursementType(),
            this.getRepriseMarchandise()
        );

        onDetailChange(qualificationDetail, errors, null);
    }

    handleChangeDonneurOrdre = (event) => {
        const { onDetailChange } = this.props;

        let newState = { ...this.state, [event.target.name]: event.target.value };

        this.setState(newState);

        const qualificationDetails = [
            {
                position: 4,
                code: ReclamationQualificationDetailCodes.ContactClientNom,
                type: ReclamationQualificationDetailType.Texte,
                libelle: "Contact, nom",
                valeurString: newState.donneurOrdreNom
            },
            {
                position: 4,
                code: ReclamationQualificationDetailCodes.ContactClientEmail,
                type: ReclamationQualificationDetailType.Texte,
                libelle: "Contact, email",
                valeurString: newState.donneurOrdreMail
            },
            {
                position: 4,
                code: ReclamationQualificationDetailCodes.ContactClientTelephone,
                type: ReclamationQualificationDetailType.Texte,
                libelle: "Contact, téléphone",
                valeurString: newState.donneurOrdreTelephone
            },
        ];

        const errors = this.checkErrors(
            newState,
            this.getModeRetour(),
            this.getCodeDepot(),
            this.getClientInforme(),
            this.getModePriseEnCharge(),
            this.getModePriseEnChargeATC(),
            this.getMagasinRetourATC(),
            this.getEmails(),
            this.getPlateformeRetour(),
            this.getArticlesEnEtatRevente(),
            this.getRemboursementType(),
            this.getRepriseMarchandise()
        );

        onDetailChange(qualificationDetails, errors, null);
    }

    handleQualificationDetailsCodeDepot = (event, codeDepot, depot) => {
        const { onDetailChange } = this.props;

        const qualificationDetail = {
            position: 2,
            code: ReclamationQualificationDetailCodes.Depot,
            type: ReclamationQualificationDetailType.CodeDepot,
            libelle: "Magasin",
            valeurString: codeDepot,
            valeurObject: depot
        };

        //console.log("handleQualificationDetailsCodeDepot", codeDepot, depot);

        // On recalcul les frais de retour.
        this.getFraisRetour(this.getModeRetour(), this.getModePriseEnCharge(), depot, this.state.declaratifRebut);

        const errors = this.checkErrors(
            null,
            this.getModeRetour(),
            codeDepot,
            this.getClientInforme(),
            this.getModePriseEnCharge(),
            this.getModePriseEnChargeATC(),
            this.getMagasinRetourATC(),
            this.getEmails(),
            this.getPlateformeRetour(),
            this.getArticlesEnEtatRevente(),
            this.getRemboursementType(),
            this.getRepriseMarchandise()
        );

        onDetailChange(qualificationDetail, errors, null);

        // On vérifie si la qualification `Le client dépose la marchandise dans un magasin` a été sélectionné pour vérifier les articles dans le plv
        let modeRetour = this.getModeRetour();

        if (modeRetour === 1) {
            this.handleCheckArticlesPLV(codeDepot);
        }
    }

    handleQualificationDetailsMagasinRetourATC = (event, magasinRetourATC) => {
        const { onDetailChange } = this.props;

        const qualificationDetail = {
            position: 3,
            code: ReclamationQualificationDetailCodes.MagasinRetourATC,
            type: ReclamationQualificationDetailType.CodeDepot,
            libelle: "Magasin",
            valeurString: magasinRetourATC
        };

        const errors = this.checkErrors(
            null,
            this.getModeRetour(),
            this.getCodeDepot(),
            this.getClientInforme(),
            this.getModePriseEnCharge(),
            this.getModePriseEnChargeATC(),
            magasinRetourATC,
            this.getEmails(),
            this.getPlateformeRetour(),
            this.getArticlesEnEtatRevente(),
            this.getRemboursementType(),
            this.getRepriseMarchandise()
        );

        onDetailChange(qualificationDetail, errors, null);
    }

    handleQualificationDetailsPlateformeRetour = (event, plateformeRetourCode, plateformeRetourObjet) => {
        const { onDetailChange } = this.props;

        let adresse = '';
        if (plateformeRetourObjet !== '') {
            adresse = 'FOUSSIER\n' + plateformeRetourObjet.adresse + '\n' + plateformeRetourObjet.codePostal + ' ' + plateformeRetourObjet.ville;
        }

        const qualificationDetails = [
            {
                position: 3,
                code: ReclamationQualificationDetailCodes.PlateformeRetour,
                type: ReclamationQualificationDetailType.CodeDepot,
                libelle: "Plateforme",
                valeurString: plateformeRetourCode
            },
            {
                position: 4,
                code: ReclamationQualificationDetailCodes.AdresseRetour,
                type: ReclamationQualificationDetailType.Texte,
                libelle: "Adresse retour",
                valeurString: adresse
            }
        ];

        const errors = this.checkErrors(
            null,
            this.getModeRetour(),
            this.getCodeDepot(),
            this.getClientInforme(),
            this.getModePriseEnCharge(),
            this.getModePriseEnChargeATC(),
            this.getMagasinRetourATC,
            this.getEmails(),
            plateformeRetourCode,
            this.getArticlesEnEtatRevente(),
            this.getRemboursementType(),
            this.getRepriseMarchandise()
        );

        onDetailChange(qualificationDetails, errors, null);
    }

    handleQualificationDetailsModePriseEnChargeChange = async (event) => {
        const { onDetailChange } = this.props;

        const qualificationDetails = [{
            position: 2,
            code: ReclamationQualificationDetailCodes.ModePriseEnCharge,
            type: ReclamationQualificationDetailType.ChoixSimple,
            libelle: modePriseEnChargeLabel,
            valeurString: modePriseEnChargeLabels[event.target.value],
            valeurInteger: parseInt(event.target.value, 10)
        }];

        let detailsToDelete = [];

        if (event.target.value === '0') {
            detailsToDelete.push(ReclamationQualificationDetailCodes.AdresseEnlevement);
            detailsToDelete.push(ReclamationQualificationDetailCodes.AdresseDestination);
            detailsToDelete.push(ReclamationQualificationDetailCodes.ContactClient);
            detailsToDelete.push(ReclamationQualificationDetailCodes.DateEnlevement);
            detailsToDelete.push(ReclamationQualificationDetailCodes.NombreColis);
            detailsToDelete.push(ReclamationQualificationDetailCodes.NombrePalettes);
            detailsToDelete.push(ReclamationQualificationDetailCodes.NombreLongueurs);
        }
        else if (event.target.value === '1') {
            qualificationDetails.push(
                {
                    position: 3,
                    code: ReclamationQualificationDetailCodes.AdresseEnlevement,
                    type: ReclamationQualificationDetailType.Texte,
                    libelle: "Adresse d'enlèvement",
                    valeurString: this.adresseEnlevementToStr(this.state)
                });
            qualificationDetails.push(
                {
                    position: 4,
                    code: ReclamationQualificationDetailCodes.AdresseDestination,
                    type: ReclamationQualificationDetailType.Texte,
                    libelle: "Adresse de destination",
                    valeurString: this.adresseDestinationToStr(this.state),
                    valeurInteger: this.state.adresseDestinationCode
                });
            qualificationDetails.push(
                {
                    position: 5,
                    code: ReclamationQualificationDetailCodes.ContactClientNom,
                    type: ReclamationQualificationDetailType.Texte,
                    libelle: "Contact, nom",
                    valeurString: this.state.donneurOrdreNom
                });

            qualificationDetails.push(
                {
                    position: 6,
                    code: ReclamationQualificationDetailCodes.ContactClient,
                    type: ReclamationQualificationDetailType.Texte,
                    libelle: "Contact",
                    valeurString: this.state.donneurOrdreMail
                });

            qualificationDetails.push(
                {
                    position: 7,
                    code: ReclamationQualificationDetailCodes.ContactClient,
                    type: ReclamationQualificationDetailType.Texte,
                    libelle: "Contact",
                    valeurString: this.state.donneurOrdreTelephone
                });
            if (this.state[ReclamationQualificationDetailCodes.DateEnlevement] !== null) {
                qualificationDetails.push(
                    {
                        position: 8,
                        code: ReclamationQualificationDetailCodes.DateEnlevement,
                        type: ReclamationQualificationDetailType.Date,
                        libelle: qualificationDetailLabels[ReclamationQualificationDetailCodes.DateEnlevement],
                        valeurDate: this.state[ReclamationQualificationDetailCodes.DateEnlevement].toDateString()
                    });
            }
            if (this.state[ReclamationQualificationDetailCodes.Poids]) {
                qualificationDetails.push(
                    {
                        position: 9,
                        code: ReclamationQualificationDetailCodes.Poids,
                        type: ReclamationQualificationDetailType.Nombre,
                        libelle: qualificationDetailLabels[ReclamationQualificationDetailCodes.Poids],
                        valeurInteger: this.state[ReclamationQualificationDetailCodes.Poids]
                    });
            }
            if (this.state[ReclamationQualificationDetailCodes.NombreColis]) {
                qualificationDetails.push(
                    {
                        position: 10,
                        code: ReclamationQualificationDetailCodes.NombreColis,
                        type: ReclamationQualificationDetailType.Nombre,
                        libelle: qualificationDetailLabels[ReclamationQualificationDetailCodes.NombreColis],
                        valeurInteger: this.state[ReclamationQualificationDetailCodes.NombreColis]
                    });
            }
            if (this.state[ReclamationQualificationDetailCodes.NombrePalettes]) {
                qualificationDetails.push(
                    {
                        position: 11,
                        code: ReclamationQualificationDetailCodes.NombrePalettes,
                        type: ReclamationQualificationDetailType.Nombre,
                        libelle: qualificationDetailLabels[ReclamationQualificationDetailCodes.NombrePalettes],
                        valeurInteger: this.state[ReclamationQualificationDetailCodes.NombrePalettes]
                    });
            }
            if (this.state[ReclamationQualificationDetailCodes.NombreLongueurs]) {
                qualificationDetails.push(
                    {
                        position: 12,
                        code: ReclamationQualificationDetailCodes.NombreLongueurs,
                        type: ReclamationQualificationDetailType.Nombre,
                        libelle: qualificationDetailLabels[ReclamationQualificationDetailCodes.NombreLongueurs],
                        valeurInteger: this.state[ReclamationQualificationDetailCodes.NombreLongueurs]
                    });
            }
        }

        const value = event.target.value;

        // On recalcul les frais de retour.
        this.getFraisRetour(this.getModeRetour(), value, this.getDepot(), this.state.declaratifRebut);

        const errors = this.checkErrors(
            null,
            this.getModeRetour(),
            this.getCodeDepot(),
            this.getClientInforme(),
            parseInt(value, 10),
            this.getModePriseEnChargeATC(),
            this.getMagasinRetourATC(),
            this.getEmails(),
            this.getPlateformeRetour(),
            this.getArticlesEnEtatRevente(),
            this.getRemboursementType(),
            this.getRepriseMarchandise()
        );

        onDetailChange(qualificationDetails, errors, detailsToDelete);
    }

    handleQualificationDetailsModePriseEnChargeATCChange = (event) => {
        const { onDetailChange, reclamation } = this.props;

        const qualificationDetails = [{
            position: 2,
            code: ReclamationQualificationDetailCodes.ModePriseEnChargeATC,
            type: ReclamationQualificationDetailType.ChoixSimple,
            libelle: modePriseEnChargeATCLabel,
            valeurString: modePriseEnChargeATCLabels[event.target.value],
            valeurInteger: parseInt(event.target.value, 10)
        }];

        let detailsToDelete = [];


        const value = event.target.value;

        let magasinRetourATC = '';

        if (value === '0') {

            magasinRetourATC = reclamation.magasin ? reclamation.magasin.codeDepot : '';

            qualificationDetails.push(
                {
                    position: 3,
                    code: ReclamationQualificationDetailCodes.MagasinRetourATC,
                    type: ReclamationQualificationDetailType.CodeDepot,
                    libelle: "Magasin",
                    valeurString: magasinRetourATC
                });
        }

        if (value === '1') {
            detailsToDelete.push(ReclamationQualificationDetailCodes.MagasinRetourATC);
        }

        // On recalcul les frais de retour.
        this.getFraisRetour(this.getModeRetour(), value, this.getDepot(), this.state.declaratifRebut);

        const errors = this.checkErrors(
            null,
            this.getModeRetour(),
            this.getCodeDepot(),
            this.getClientInforme(),
            this.getModePriseEnCharge(),
            parseInt(value, 10),
            magasinRetourATC,
            this.getEmails(),
            this.getPlateformeRetour(),
            this.getArticlesEnEtatRevente(),
            this.getRemboursementType(),
            this.getRepriseMarchandise()
        );

        onDetailChange(qualificationDetails, errors, detailsToDelete);
    }

    handleQualificationDetailsClientInformeChange = ({ checked, position, libelle }) => {
        const { onDetailChange } = this.props;

        //console.log("handleQualificationDetailsClientInformeChange", checked);
        //console.log("handleQualificationDetailsClientInformeChange", position);
        //console.log("handleQualificationDetailsClientInformeChange", libelle);

        const qualificationDetail = {
            position: position,
            code: ReclamationQualificationDetailCodes.ClientInforme,
            type: ReclamationQualificationDetailType.Booleen,
            libelle: libelle,
            valeurBool: checked
        };

        const errors = this.checkErrors(
            null,
            this.getModeRetour(),
            this.getCodeDepot(),
            checked,
            this.getModePriseEnCharge(),
            this.getModePriseEnChargeATC(),
            this.getMagasinRetourATC(),
            this.getEmails(),
            this.getPlateformeRetour(),
            this.getArticlesEnEtatRevente(),
            this.getRemboursementType(),
            this.getRepriseMarchandise()
        );

        onDetailChange(qualificationDetail, errors, null);
    }

    handleQualificationDetailsEmails = (emails) => {
        const { onDetailChange } = this.props;

        let qualificationDetail = null;

        if (emails && emails !== '') {
            qualificationDetail = {
                position: 25,
                code: ReclamationQualificationDetailCodes.Emails,
                type: ReclamationQualificationDetailType.Texte,
                libelle: 'Email(s) client pour notification de la reprise',
                valeurString: emails
            };
        }

        const errors = this.checkErrors(
            null,
            this.getModeRetour(),
            this.getCodeDepot(),
            this.getClientInforme(),
            this.getModePriseEnCharge(),
            this.getModePriseEnChargeATC(),
            this.getMagasinRetourATC(),
            emails,
            this.getPlateformeRetour(),
            this.getArticlesEnEtatRevente(),
            this.getRemboursementType(),
            this.getRepriseMarchandise()
        );

        onDetailChange(qualificationDetail, errors, null);
    }

    handleJokerFraisRetourChange = (event) => {
        if (event.target.checked) {
            this.setState({
                warningFraisRetour: true
            });
        }

        this.props.onJokerFraisRetourChange();
    }

    handleIgnoreWarningFraisRetour = () => {
        this.setState({
            warningFraisRetour: false
        });
    }

    handleWarningCancel = () => {
        this.setState({
            warningFraisRetour: false
        });

        this.props.onJokerFraisRetourChange();
    }

    handleWarningCancelMontantFaible = () => {
        this.props.closeAddReclamationDialog();
    }

    handleIgnoreWarningMontantFaible = () => {
        this.setState({ eligibiliteNonMontantFaibleDialogOpen: false });
    }

    handleIgnoreWarningOkSeulementMagasin = () => {
        this.setState({ eligibiliteOkSeulementMagasinDialogOpen: false });
    }

    handleQualificationDetailArticlesEnEtatRevente = (event) => {
        const { onDetailChange, reclamation } = this.props;

        let newState = { ...this.state, articlesEnEtatRevente: event.target.value };

        const errors = this.checkErrors(
            null,
            this.getModeRetour(),
            this.getCodeDepot(),
            this.getClientInforme(),
            this.getModePriseEnCharge(),
            this.getModePriseEnChargeATC(),
            this.getMagasinRetourATC(),
            this.getEmails(),
            this.getPlateformeRetour(),
            event.target.value === 'true',
            this.getRemboursementType(),
            this.getRepriseMarchandise()
        );

        let detailsToDelete = [];

        const qualificationDetail = {
            position: 3,
            code: ReclamationQualificationDetailCodes.Rebut,
            type: ReclamationQualificationDetailType.Booleen,
            libelle: "Articles en état de revente ?",
            valeurBool: event.target.value === 'true'
        };

        // Si on recoche `Oui` on vide le tableau des articles en rebut
        if (event.target.value === 'true') {
            // detailsToDelete.push(ReclamationQualificationDetailCodes.Rebut);
            detailsToDelete.push(ReclamationQualificationDetailCodes.RebutArticlesDeclares);

            newState = { ...newState, declaratifRebut: [], rebutValider: false };

            this.getFraisRetour(this.getModeRetour(), this.getModePriseEnCharge(), this.getDepot(), []);

        } else {
            const articlesRebut = reclamation.lignes
                .filter(article => article.quantiteAnomalie > 0)
                .map((article) => {
                    return {
                        id: article.id,
                        codeArticle: article.codeArticle,
                        quantiteAnomalie: article.quantiteAnomalie,
                        prixUnitaire: article.prixUnitaire,
                        quantiteRebut: 0,
                        errorQuantiteRebut: false,
                    };
                });

            newState = { ...newState, declaratifRebut: articlesRebut };

            this.getFraisRetour(this.getModeRetour(), this.getModePriseEnCharge(), this.getDepot(), articlesRebut);
        }

        this.setState(newState);

        onDetailChange(qualificationDetail, errors, detailsToDelete);
    }

    handleChangeDeclaratifRebut = (event, article) => {

        const { declaratifRebut } = this.state;

        if (declaratifRebut.length === 0) {
            this.setState(prevState => ({
                declaratifRebut: [...prevState.declaratifRebut, article]
            }));

            this.getFraisRetour(this.getModeRetour(), this.getModePriseEnCharge(), this.getDepot(), [article]);

        } else {
            const newArray = declaratifRebut.map(art => {
                if (art.codeArticle === article.codeArticle) {

                    let erreur = true;
                    let quantite = parseInt(event.target.value);
                    if (!isNaN(quantite) && quantite >= 0 && quantite <= art.quantiteAnomalie) {
                        erreur = false;
                    }

                    return {
                        codeArticle: art.codeArticle,
                        quantiteAnomalie: art.quantiteAnomalie,
                        prixUnitaire: art.prixUnitaire,
                        quantiteRebut: event.target.value,
                        errorQuantiteRebut: erreur,
                    }

                } else {
                    return art;
                }

            });

            this.getFraisRetour(this.getModeRetour(), this.getModePriseEnCharge(), this.getDepot(), newArray);

            this.setState({ ...this.state, declaratifRebut: newArray });
        }
    }

    handleQualificationDetailChangeRemboursementType = (event) => {
        const { onDetailChange } = this.props;

        let value = parseInt(event.target.value);
        if (!isNaN(value)) {

            let newState = { ...this.state, remboursementType: value };

            const errors = this.checkErrors(
                null,
                this.getModeRetour(),
                this.getCodeDepot(),
                this.getClientInforme(),
                this.getModePriseEnCharge(),
                this.getModePriseEnChargeATC(),
                this.getMagasinRetourATC(),
                this.getEmails(),
                this.getPlateformeRetour(),
                this.getArticlesEnEtatRevente(),
                value,
                null
            );

            const qualificationDetail = {
                position: 26,
                code: ReclamationQualificationDetailCodes.TypeRemboursement,
                type: ReclamationQualificationDetailType.ChoixSimple,
                libelle: "Type de remboursement",
                valeurInteger: value,
                valeurString: typeRemboursementLabels[value]
            };

            newState.repriseMarchandise = null;


            this.setState(newState);

            onDetailChange(qualificationDetail, errors, null);
        }
    }

    handleQualificationDetailChangeRepriseMarchandise = (event) => {
        const { onDetailChange } = this.props;

        let value = parseInt(event.target.value);
        if (!isNaN(value)) {

            let newState = { ...this.state, repriseMarchandise: value };

            const errors = this.checkErrors(
                null,
                this.getModeRetour(),
                this.getCodeDepot(),
                this.getClientInforme(),
                this.getModePriseEnCharge(),
                this.getModePriseEnChargeATC(),
                this.getMagasinRetourATC(),
                this.getEmails(),
                this.getPlateformeRetour(),
                this.getArticlesEnEtatRevente(),
                this.getRemboursementType(),
                value,
            );

            const qualificationDetail = {
                position: 27,
                code: ReclamationQualificationDetailCodes.RepriseMarchandise,
                type: ReclamationQualificationDetailType.ChoixSimple,
                libelle: "Reprise de la marchandise",
                valeurInteger: value,
                valeurString: typeRepriseMarchandiseLabels[value]
            };

            this.setState(newState);

            onDetailChange(qualificationDetail, errors, null);
        }
    }

    handleValiderArticlesRebut = () => {

        const { onDetailChange } = this.props;

        const { declaratifRebut } = this.state;

        const articlesRebutsDeclares = declaratifRebut
            .filter(x => x.quantiteRebut && x.quantiteRebut > 0)
            .map(x => {
                return {
                    code: x.codeArticle,
                    quantite: x.quantiteRebut,
                }
            });

        let detailsToDelete = [];

        const qualificationDetail = {
            position: 4,
            code: ReclamationQualificationDetailCodes.RebutArticlesDeclares,
            type: ReclamationQualificationDetailType.CodeArticlesQuantites,
            libelle: "Article(s) déclaré(s) en rebut",
            valeurString: JSON.stringify(articlesRebutsDeclares)
        };

        this.setState({ ...this.state, rebutValider: true });

        onDetailChange(qualificationDetail, null, detailsToDelete);
    }

    handleCheckArticlesPLV = async (codeDepot) => {
        const { onClickCheckArticlesPLV } = this.props;

        let result = await onClickCheckArticlesPLV(codeDepot);

        this.setState({ ...this.state, plvMagasin: result });

    }



    renderHeader = () => {
        const { classes, reclamation } = this.props;

        return (
            <>

                <div className={classes.row}>
                    <TextField
                        multiline
                        fullWidth
                        name="commentaire"
                        label="Commentaire"
                        placeholder="Décrire le problème rencontré"
                        maxRows="3"
                        onChange={this.handleChange}
                        value={reclamation.commentaire}
                        InputLabelProps={{
                            shrink: true
                        }}
                    />
                </div>

                <div className={classes.row}>
                    <DropZone id="fichiersIds" name="fichiersIds" onChange={this.handleChange} />
                </div>
            </>
        );
    }

    renderPieceGestionSearch = () => {
        const { classes, reclamation, namespace } = this.props;

        const { expandedPanel } = this.state;

        return (
            <div className={classes.row}>

                <Accordion expanded={expandedPanel === 'panelPiece'} elevation={2} onChange={this.handleChangePanel('panelPiece')}>

                    <QualificationAccordionSummary
                        title="Rechercher une pièce* (obligatoire pour accéder aux lignes)"
                        fontSize='large'
                        fontWeight='lighter'
                    />

                    <AccordionDetails>
                        <PieceGestionSearch
                            onSelectPiece={this.handleSelectPiece}
                            client={reclamation.client}
                            withLignes={false}
                            namespace={namespace}
                            warningReclamation
                            defautProduitInfos={false}
                            logistiqueInfos
                            defautNumeroPiece={reclamation.numeroPiece}
                        />
                    </AccordionDetails>
                </Accordion>
            </div>
        );
    }

    // DEV-4012 : accordéon « Type de retour » (2 cards radio BL / Commande), affiché après sélection d'un BL.
    // La card commande est grisée pour les profils magasin ou si le BL n'a pas de commande d'origine.
    renderTypeRetour = () => {
        const { classes, utilisateurMagasin } = this.props;
        const { pieceSelectionnee, typeRetour, expandedPanel } = this.state;

        if (!pieceSelectionnee) {
            return null;
        }

        const commandeGestionCle = pieceSelectionnee.commandeGestionCle;
        const commandeOrigine = pieceSelectionnee.commandeOrigine;
        // Commande possible si le BL est rattaché à une commande et hors profil magasin.
        const commandeDisponible = Boolean(commandeGestionCle) && !utilisateurMagasin;

        let commandeRaisonIndispo = null;
        if (!commandeGestionCle) {
            commandeRaisonIndispo = "Ce bon de livraison n'est rattaché à aucune commande d'origine.";
        } else if (utilisateurMagasin) {
            commandeRaisonIndispo = "Le retour depuis la commande n'est pas autorisé pour les profils magasin.";
        }

        const cardBl = (
            <Paper
                variant="outlined"
                onClick={() => this.handleSelectTypeRetour(TypePiece.BonLivraison)}
                sx={{
                    flex: 1,
                    p: 2,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    borderColor: typeRetour === TypePiece.BonLivraison ? 'secondary.main' : undefined,
                    borderWidth: typeRetour === TypePiece.BonLivraison ? 2 : 1,
                    backgroundColor: typeRetour === TypePiece.BonLivraison ? 'rgba(255, 152, 0, 0.08)' : undefined,
                }}
            >
                <Radio checked={typeRetour === TypePiece.BonLivraison} />
                <Inventory2OutlinedIcon sx={{ color: 'action.active', mt: 0.5 }} />
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Retour sur le Bon de Livraison</Typography>
                    <Typography variant="body2" color="text.secondary">Le retour porte sur la pièce (BL) sélectionnée.</Typography>
                    <Typography variant="body2" color="text.secondary">Les lignes du BL sont directement accessibles pour sélection.</Typography>
                </Box>
            </Paper>
        );

        const cardCommande = (
            <Paper
                variant="outlined"
                onClick={commandeDisponible ? () => this.handleSelectTypeRetour(TypePiece.Commande) : undefined}
                sx={{
                    flex: 1,
                    p: 2,
                    cursor: commandeDisponible ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    opacity: commandeDisponible ? 1 : 0.5,
                    borderColor: typeRetour === TypePiece.Commande ? 'secondary.main' : undefined,
                    borderWidth: typeRetour === TypePiece.Commande ? 2 : 1,
                    backgroundColor: typeRetour === TypePiece.Commande ? 'rgba(255, 152, 0, 0.08)' : undefined,
                }}
            >
                <Radio checked={typeRetour === TypePiece.Commande} disabled={!commandeDisponible} />
                <DescriptionOutlinedIcon sx={{ color: 'action.active', mt: 0.5 }} />
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: typeRetour === TypePiece.Commande ? 'secondary.main' : undefined }}>
                        Retour depuis la commande
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Le retour porte sur l'ensemble de la commande d'origine{commandeOrigine ? ` (${commandeOrigine})` : ''}.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Toutes les pièces de la commande sont proposées à la sélection.
                    </Typography>
                </Box>
            </Paper>
        );

        return (
            <div className={classes.row}>
                <Accordion expanded={expandedPanel === 'panelTypeRetour'} elevation={2} onChange={this.handleChangePanel('panelTypeRetour')}>

                    <QualificationAccordionSummary
                        title="Type de retour* (obligatoire pour accéder aux lignes)"
                        fontSize='large'
                        fontWeight='lighter'
                    />

                    <AccordionDetails>
                        <div style={{ width: '100%' }}>
                            <Typography variant="body2" sx={{ mb: 2 }}>Choisissez le mode de retour pour cette pièce :</Typography>
                            <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                                {cardBl}
                                {commandeDisponible
                                    ? cardCommande
                                    : <Tooltip title={commandeRaisonIndispo || ''}><span style={{ flex: 1, display: 'flex' }}>{cardCommande}</span></Tooltip>}
                            </Stack>
                        </div>
                    </AccordionDetails>
                </Accordion>
            </div>
        );
    }

    renderLigneGestionQualification = () => {
        const { classes, reclamation, auMoinsUnRetourExclu, aucunRetourPossible, loadingLignes, onClose } = this.props;

        const { expandedPanel, etape, eligibilite, seulementMagasinFlags, eligibiliteNonMontantFaibleDialogOpen, exclusionFrais, eligibiliteOkSeulementMagasinDialogOpen } = this.state;

        const clientInformeValue = this.getClientInforme();

        let exclusionFraisMessage = exclusionFrais === ExclusionFraisRetourVolontaire.Aucune ? '' : "Il n'y aura pas de frais de retour pour cette pièce ";

        if (exclusionFrais === ExclusionFraisRetourVolontaire.Client) {
            exclusionFraisMessage += "car le client est exclu des frais de retour."
        }
        else if (exclusionFrais === ExclusionFraisRetourVolontaire.Activite) {
            exclusionFraisMessage += "car l'activité du client est exclue des frais de retour."
        }
        else if (exclusionFrais === ExclusionFraisRetourVolontaire.ModeLivraisonProfil) {
            exclusionFraisMessage += "car le mode de livraison et le PO de la pièce l'exclue des frais de retour."
        }
        else if (exclusionFrais === ExclusionFraisRetourVolontaire.Famille) {
            exclusionFraisMessage += "car tous les articles de la pièce font partis de familles exclues des frais de retour."
        }
        else if (exclusionFrais === ExclusionFraisRetourVolontaire.MontantMiniClient) {
            exclusionFraisMessage += "car elle à moins de 15 jours et que le client est exclu du montant minimum de retour."
        }
        else if (exclusionFrais === ExclusionFraisRetourVolontaire.MontantMiniActivite) {
            exclusionFraisMessage += "car elle à moins de 15 jours et que l'activité du client est exclue du montant minimum de retour."
        }
        else if (exclusionFrais === ExclusionFraisRetourVolontaire.MontantMiniATC) {
            exclusionFraisMessage += "car elle à moins de 15 jours et que l'ATC du client est exclu du montant minimum de retour."
        }

        const eligibiliteOK = eligibilite === EligibiliteRetourVolontaire.OK || eligibilite === EligibiliteRetourVolontaire.OKSeulementMagasin;

        return (
            <>
                <div className={classes.row}>
                    <Accordion expanded={expandedPanel === 'panelLignes'} elevation={2} onChange={this.handleChangePanel('panelLignes')}>

                        <QualificationAccordionSummary
                            title="Sélectionner une ligne* (au moins une ligne en anomalie pour pouvoir créer le dossier après-vente)"
                            fontSize='large'
                            fontWeight='lighter'
                        />


                        <AccordionDetails className={classes.flexContainer}>
                            <div className={classes.row}>
                                <LigneGestionQualification
                                    reclamation={reclamation}
                                    onAddLigne={this.handleAddLigne}
                                    onRemoveLigne={this.handleRemoveLigne}
                                    typeAnomalie={AnomalieType.DemandeRetourVolontaire}
                                    disabled={etape !== Etape.ChoixDesLignes}
                                    afficherQuantiteRetournable={this.state.typeRetour !== null}
                                />
                            </div>

                            {etape === Etape.ChoixDesLignes && !loadingLignes && !aucunRetourPossible && (
                                <div className={classes.row}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={this.handleSelectAllLignes}
                                        disabled={this.state.controleEligibiliteEnCours}
                                    >
                                        Tout retourner
                                    </Button>
                                    {auMoinsUnRetourExclu && <span style={{ marginLeft: '8px' }}>Sauf les articles non autorisés en retour.</span>}
                                </div>
                            )}

                            {etape === Etape.ChoixDesLignes && !loadingLignes && aucunRetourPossible && (
                                <div className={classes.row}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={onClose}
                                    >
                                        Abandonner
                                    </Button>
                                    <span style={{ marginLeft: '8px' }}>Aucun retour possible</span>

                                </div>
                            )}

                            {etape === Etape.ChoixDesLignes && reclamation.lignes.length > 0 && (
                                <div className={classes.row}>
                                    <LoadingButton
                                        disabled={false}
                                        onClick={this.handleClickCheckEligibilite}
                                        label="Vérifier éligibilité du retour"
                                        pending={this.state.controleEligibiliteEnCours}
                                    />
                                </div>
                            )}

                            {etape === Etape.EligibiliteResult && (
                                <div className={classes.row}>
                                    {eligibiliteOK && (
                                        <p>Le retour est éligible</p>
                                    )}

                                    {eligibilite === EligibiliteRetourVolontaire.NonCauseRapido && (
                                        <React.Fragment>
                                            <p>{eligibiliteNonCauseRapidoLabel}</p>

                                            <p>Créer = Affectation GT Rapido, notifications aux pilotes et clôture automatique</p>
                                        </React.Fragment>
                                    )}

                                    {eligibilite === EligibiliteRetourVolontaire.NonCauseLivraisonDirecte && (
                                        <React.Fragment>
                                            <p>{eligibiliteNonCauseLivraisonDirecteLabel}</p>

                                            <p>Créer = Affectation au sédentaire du client</p>
                                        </React.Fragment>
                                    )}

                                    {eligibilite === EligibiliteRetourVolontaire.NonCauseTR && (
                                        <React.Fragment>
                                            <p>{eligibiliteNonCauseTRLabel}</p>

                                            <p>Créer = Affectation au sédentaire du client</p>
                                        </React.Fragment>
                                    )}

                                    {eligibilite === EligibiliteRetourVolontaire.NonArticleExclu && (
                                        <React.Fragment>
                                            <p>{eligibiliteNonArticleExcluLabel}</p>
                                        </React.Fragment>
                                    )}

                                    {eligibilite === EligibiliteRetourVolontaire.NonMontantFaible && (
                                        <React.Fragment>
                                            <p>{eligibiliteNonMontantFaibleLabel}</p>

                                            <Dialog open={eligibiliteNonMontantFaibleDialogOpen}>
                                                <DialogTitle>{"Avertissement"}</DialogTitle>

                                                <DialogContent>
                                                    <DialogContentText>
                                                        {eligibiliteNonMontantFaibleLabel}
                                                    </DialogContentText>
                                                </DialogContent>
                                                <DialogActions>
                                                    <Button
                                                        onClick={this.handleWarningCancelMontantFaible}
                                                        color="primary"
                                                    >
                                                        Annuler
                                                    </Button>
                                                    <Button
                                                        onClick={this.handleIgnoreWarningMontantFaible}
                                                        variant="contained"
                                                        color="secondary"
                                                    >
                                                        Poursuivre
                                                    </Button>
                                                </DialogActions>
                                            </Dialog>

                                            <FormControlLabel className={classes.row}
                                                control={
                                                    <Checkbox
                                                        checked={clientInformeValue}
                                                        onChange={e => this.handleQualificationDetailsClientInformeChange({ checked: e.target.checked, position: 24, libelle: clientInformeEligibiliteNonLabel })}
                                                        value="true"
                                                        name={ReclamationQualificationDetailCodes.ClientInforme}
                                                    />
                                                }
                                                label={clientInformeEligibiliteNonLabel}
                                            />
                                            <p>Créer = clôture automatique</p>
                                        </React.Fragment>
                                    )}

                                    {eligibilite === EligibiliteRetourVolontaire.OKSeulementMagasin && (
                                        <React.Fragment>
                                            <p><span dangerouslySetInnerHTML={{ __html: eligibiliteOkSeulementMagasinLabel(seulementMagasinFlags) }} /></p>
                                            
                                            <Dialog open={eligibiliteOkSeulementMagasinDialogOpen}>
                                                <DialogTitle>{"Information"}</DialogTitle>

                                                <DialogContent>
                                                    <DialogContentText>
                                                        <span dangerouslySetInnerHTML={{ __html: eligibiliteOkSeulementMagasinLabel(seulementMagasinFlags) }} />
                                                    </DialogContentText>
                                                </DialogContent>
                                                <DialogActions>
                                                    <Button
                                                        onClick={this.handleIgnoreWarningOkSeulementMagasin}
                                                        variant="contained"
                                                        color="secondary"
                                                    >
                                                        Ok
                                                    </Button>
                                                </DialogActions>
                                            </Dialog>

                                        </React.Fragment>
                                    )}


                                    {eligibilite === EligibiliteRetourVolontaire.NonCauseClientFerme && (
                                        <React.Fragment>
                                            <p>{eligibiliteNonCauseClientFermeLabel}</p>

                                            <p>Créer = Affectation au sédentaire du client et clôture automatique</p>
                                        </React.Fragment>
                                    )}

                                    {eligibilite === EligibiliteRetourVolontaire.NonQuantiteRetournableDepassee && (
                                        <p>{eligibiliteNonQuantiteRetournableDepasseeLabel}</p>
                                    )}


                                    {eligibilite !== EligibiliteRetourVolontaire.NonCauseClientFerme && (
                                        <div className={classes.row}>
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                onClick={this.handleClickEditLignes}
                                            >
                                                Modifier la demande
                                            </Button>
                                        </div>
                                    )}

                                    {eligibiliteOK && exclusionFraisMessage && (
                                        <div className={classes.row}>
                                            <p>{exclusionFraisMessage}</p>
                                        </div>
                                    )}

                                    {eligibiliteOK && (
                                        <div className={classes.row}>
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                onClick={this.handleClickTreatReturn}
                                            >
                                                Traiter le retour
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            )}

                        </AccordionDetails>
                    </Accordion>
                </div>
            </>
        );
    }

    renderTraitementRetour = () => {
        const { classes } = this.props;

        const { expandedPanel, etape } = this.state;

        return (
            <div className={classes.row}>
                {etape === Etape.TraitementRetour && (

                    <Accordion expanded={expandedPanel === 'panelTraitementRetour'} elevation={2} onChange={this.handleChangePanel('panelTraitementRetour')}>

                        <QualificationAccordionSummary
                            title="Formulaire de retour"
                            fontSize='large'
                            fontWeight='lighter'
                        />

                        <AccordionDetails className={classes.flexContainer}>

                            {this.renderFormulaireRetour()}
                        </AccordionDetails>
                    </Accordion>
                )}
            </div>
        );
    }

    renderWarningAgePieceSansExclusionDecote = () => {
        const contenu = "Attention cette pièce a une date de livraison supérieure à 90 jours, une décote 100% sera appliquée à l'ensemble des articles.\nVoulez-vous poursuivre ?"

        return (
            <MessageDialog
                titre="Attention"
                contenu={contenu}
                ouvert={true}
                init={true}

                texteNOK="Annuler"
                texteOK="Poursuivre"

                onNOK={this.props.closeAddReclamationDialog}
                onOK={() => { this.setState({ ignorerDecoteTotale: true }); }}

            />
        );
    }

    renderFormulaireRetour() {
        const { classes, reclamation, masquerModeRetourClientMagasin, utilisateurMagasin } = this.props;
        const { warningFraisRetour, decote, exclusionDecote, articlesEnEtatRevente, rebutValider, eligibilite } = this.state;

        const modeRetourValue = this.getModeRetour().toString();

        const modePriseEnChargeValue = this.getModePriseEnCharge().toString();
        const modePriseEnChargeATCValue = this.getModePriseEnChargeATC().toString();

        const codeDepot = this.getCodeDepot();
        const magasinRetourATC = this.getMagasinRetourATC();
        const plateformeRetour = this.getPlateformeRetour();


        const afficheResume =
            modeRetourValue === '0' // "Un transporteur doit venir à cette adresse"
            || (modeRetourValue === '1' && codeDepot && (!utilisateurMagasin || (articlesEnEtatRevente === 'true' || (articlesEnEtatRevente === 'false' && rebutValider)))) // "Le client dépose la marchandise dans un magasin"
            || (modeRetourValue === '2' && modePriseEnChargeValue === '0' && plateformeRetour) // "Le client souhaite prendre en charge lui-même le retour plateforme" => Le client s'occupe lui-même du transport
            || (modeRetourValue === '2' && modePriseEnChargeValue === '1') // "Le client souhaite prendre en charge lui-même le retour plateforme" => Foussier envoie une étiquette transporteur pré-payée (Express uniquement)
            || (modeRetourValue === '3' && modePriseEnChargeATCValue === '0' && magasinRetourATC)  // "Le client remet la marchandise à l'ATC" => L'ATC déposera la marchandise au magasin
            || (modeRetourValue === '3' && modePriseEnChargeATCValue === '1'); // "Le client remet la marchandise à l'ATC" => L'ATC effectuera le retour directement à la plateforme de départ


        return (
            <React.Fragment>

                <MessageDialog
                    titre="Frais de retour - A préciser au client"
                    contenu={`Merci de préciser au client:\n\nPour ce type de retour, nous devrions vous imputer ${reclamation.retour.fdp.toFixed(2)}€ de frais de retour, mais à titre exceptionnel, nous vous offrons ces frais au titre de nos bonnes relations commerciales.`}
                    ouvert={warningFraisRetour}
                    init={true}

                    texteNOK="Annuler"
                    texteOK="Poursuivre"

                    onNOK={this.handleWarningCancel}
                    onOK={this.handleIgnoreWarningFraisRetour}

                />


                <div className={classes.row}>
                    <FormControl required className={classes.formControl}>

                        <Stack
                            direction='row'
                            justifyContent="flex-start"
                            alignItems="center"
                            spacing={1}
                        >
                            <FormLabel>{modeRetourLabel}</FormLabel>

                            {eligibilite === EligibiliteRetourVolontaire.OKSeulementMagasin &&
                                <Tooltip title={messageTooltipInfoFormulaireRetour} placement="right">
                                    <IconButton>
                                        <InfoIcon />
                                    </IconButton>
                                </Tooltip>
                            }
                        </Stack>

                        <RadioGroup
                            id={ReclamationQualificationDetailCodes.ModeRetour}
                            name={ReclamationQualificationDetailCodes.ModeRetour}
                            className={classes.group}
                            value={modeRetourValue}
                            onChange={this.handleQualificationDetailsModeRetourChange}
                        >
                            {modeRetourLabels.map((reponse, index) => {

                                let display = true;
                                let disabled = false;

                                switch (index) {
                                    case 0:
                                        display = decote !== 0 || exclusionDecote !== 0;
                                        disabled = eligibilite === EligibiliteRetourVolontaire.OKSeulementMagasin;
                                        break;

                                    case 1:
                                        display = !masquerModeRetourClientMagasin;
                                        break;

                                    case 2:
                                        disabled = eligibilite === EligibiliteRetourVolontaire.OKSeulementMagasin;
                                        break;

                                    case 3:
                                        disabled = eligibilite === EligibiliteRetourVolontaire.OKSeulementMagasin;
                                        break;

                                    default:
                                        break;
                                }

                                if (display) {
                                    return (<FormControlLabel
                                        key={index}
                                        value={index.toString()}
                                        control={<Radio />} label={reponse}
                                        disabled={disabled}
                                    />);
                                }
                                return null;

                            })}
                        </RadioGroup>
                    </FormControl>
                </div>


                {modeRetourValue === '0' &&
                    (this.renderFormulaireDemandeEnlevement())
                }

                {modeRetourValue === '1' &&
                    (this.renderFormulaireRetourDepot())
                }

                {modeRetourValue === '2' &&
                    (this.renderFormulaireRetourPriseEnCharge())
                }

                {modeRetourValue === '3' &&
                    (this.renderFormulaireRetourPriseEnChargeATC())
                }

                {modeRetourValue === '2' && modePriseEnChargeValue === '1' &&
                    (this.renderFormulaireDemandeEnlevement())
                }

                {modeRetourValue === '3' && modePriseEnChargeATCValue === '0' &&
                    (this.renderFormulaireMagasinRetourATC())
                }


                {modeRetourValue === '1' && utilisateurMagasin &&
                    (this.renderRebutArticles())
                }


                {afficheResume &&
                    (this.renderRetourConditions())
                }

                {afficheResume &&
                    (this.renderFormulaireRetourConditions())
                }


                {afficheResume &&

                    <MultiSelectMailContactClient
                        onChange={(emails) => this.handleQualificationDetailsEmails(emails)}
                        clientId={reclamation.client.id}
                        label="Mail(s) pour notification de la reprise*"
                        suiviEmail={reclamation.suiviEmail}
                    />
                }

                {afficheResume && modeRetourValue === '1' && utilisateurMagasin &&
                    this.renderRemboursement()
                }

            </React.Fragment>
        );
    }

    renderFormulaireDemandeEnlevement() {
        const { classes, transporteurInitial } = this.props;

        return (
            <Grid
                container
                direction="row"
                justify="space-between"
                alignContent="stretch"
                alignItems="stretch"
                spacing={1}
                className={classes.row}
            >
                <Grid container item xs={6} className={classes.gridItem}>
                    <Paper className={classes.paper}>
                        <Typography variant="h6" gutterBottom>{`Adresse d'enlèvement :`}</Typography>
                        <Stack spacing={1}>
                            <TextField required name="adresseEnlevementNom" label="Nom" value={this.state.adresseEnlevementNom} onChange={e => this.handleChangeAdresseEnlevement(e)} fullWidth />
                            <TextField required name="adresseEnlevementAdresse1" label="Adresse" value={this.state.adresseEnlevementAdresse1} onChange={e => this.handleChangeAdresseEnlevement(e)} fullWidth />
                            <TextField name="adresseEnlevementAdresse2" label="Complément" value={!this.state.adresseEnlevementAdresse2 ? '' : this.state.adresseEnlevementAdresse2} onChange={e => this.handleChangeAdresseEnlevement(e)} fullWidth />
                            <TextField required name="adresseEnlevementCodePostal" label="Code Postal" value={this.state.adresseEnlevementCodePostal} onChange={e => this.handleChangeAdresseEnlevement(e)} fullWidth />
                            <TextField required name="adresseEnlevementVille" label="Ville" value={this.state.adresseEnlevementVille} onChange={e => this.handleChangeAdresseEnlevement(e)} fullWidth />

                        </Stack>
                    </Paper>
                </Grid>

                <Grid container item xs={6} className={classes.gridItem}>
                    <Paper className={classes.paper}>

                        <Typography variant="h6" gutterBottom>Adresse de destination :</Typography>

                        <TextField
                            id="adresseDestinationCode"
                            name="adresseDestinationCode"
                            label={"Destination"}
                            fullWidth
                            required
                            select

                            SelectProps={{
                                value: this.state.adresseDestinationCode,
                                onChange: e => this.handleChangeAdresseDestination(e)
                            }}
                        >

                            {AdresseDestinations.map(adresse => (

                                <MenuItem key={adresse.id} value={adresse.id}>{adresse.nom}</MenuItem>

                            ))}
                        </TextField>


                        {this.state.adresseDestinationCode === 3 && (
                            <Stack spacing={1} mt={1}>
                                <TextField required name="adresseDestinationNom" label="Nom" value={this.state.adresseDestinationNom} onChange={this.handleChangeAdresseDestination} fullWidth />
                                <TextField required name="adresseDestinationAdresse1" label="Adresse" value={this.state.adresseDestinationAdresse1} onChange={this.handleChangeAdresseDestination} fullWidth />
                                <TextField name="adresseDestinationAdresse2" label="Complément" value={this.state.adresseDestinationAdresse2} onChange={this.handleChangeAdresseDestination} fullWidth />
                                <TextField required name="adresseDestinationCodePostal" label="Code Postal" value={this.state.adresseDestinationCodePostal} onChange={this.handleChangeAdresseDestination} />
                                <TextField required name="adresseDestinationVille" label="Ville" value={this.state.adresseDestinationVille} onChange={this.handleChangeAdresseDestination} />
                            </Stack>
                        )}



                        {transporteurInitial && (
                            <div>

                                <Typography variant="h6" gutterBottom className={classes.marginTop}>Transporteur initial :</Typography>

                                <Typography gutterBottom>{transporteurInitial.nom} / {transporteurInitial.contrat}</Typography>

                            </div>
                        )}


                    </Paper>
                </Grid>



                <Grid container item xs={6} className={classes.gridItem}>
                    <Paper className={classes.paper}>
                        <Typography variant="h6" gutterBottom className={classes.marginTop}>Contact client nécessaire à l'enlèvement :</Typography>
                        <Stack spacing={1}>
                            <TextField required name="donneurOrdreNom" label="Nom" value={this.state.donneurOrdreNom} onChange={e => this.handleChangeDonneurOrdre(e)} fullWidth />
                            <TextField required name="donneurOrdreMail" label="Email" value={this.state.donneurOrdreMail} onChange={e => this.handleChangeDonneurOrdre(e)} fullWidth />
                            <TextField required name="donneurOrdreTelephone" label="Téléphone" value={this.state.donneurOrdreTelephone} onChange={e => this.handleChangeDonneurOrdre(e)} fullWidth />
                        </Stack>
                    </Paper>
                </Grid>

                <Grid container item xs={6} className={classes.gridItem}>
                    <Paper className={classes.paper}>
                        <Typography variant="h6" gutterBottom>Expédition :</Typography>
                        <Stack spacing={1}>


                            <MyDatePicker
                                name={ReclamationQualificationDetailCodes.DateEnlevement}
                                label="Date d'enlèvement souhaitée"
                                value={this.state[ReclamationQualificationDetailCodes.DateEnlevement]}
                                onChange={value => this.handleChangeDate({ target: { name: ReclamationQualificationDetailCodes.DateEnlevement, value: value } })}
                                disableFuture={false}
                                disablePast={true}
                                disableSunday={true}
                                fullWidth
                            />

                            <TextField
                                required
                                name={ReclamationQualificationDetailCodes.Poids}
                                label={qualificationDetailLabels[ReclamationQualificationDetailCodes.Poids]}
                                value={this.state[ReclamationQualificationDetailCodes.Poids]}
                                onChange={this.handleChangeInteger}
                                className={classes.marginTop}
                                fullWidth
                                inputProps={{ position: 6 }}
                            />

                            <Stack direction="row" spacing={1} justifyContent="space-evenly">
                                <TextField
                                    className={classes.marginTop}
                                    name={ReclamationQualificationDetailCodes.NombrePalettes}
                                    label="Nombre de palettes"
                                    type="number"
                                    value={this.state[ReclamationQualificationDetailCodes.NombrePalettes]}
                                    onChange={this.handleChangeInteger}
                                    inputProps={{ position: 7 }}
                                />
                                <TextField
                                    className={classes.marginTop}
                                    name={ReclamationQualificationDetailCodes.NombreColis}
                                    label="Nombre de colis"
                                    type="number"
                                    value={this.state[ReclamationQualificationDetailCodes.NombreColis]}
                                    onChange={this.handleChangeInteger}
                                    inputProps={{ position: 8 }}
                                />
                                <TextField
                                    className={classes.marginTop}
                                    name={ReclamationQualificationDetailCodes.NombreLongueurs}
                                    label="Nombre de longueur > 1,20 m"
                                    type="number"
                                    value={this.state[ReclamationQualificationDetailCodes.NombreLongueurs]}
                                    onChange={this.handleChangeInteger}
                                    inputProps={{ position: 9 }}
                                />
                            </Stack>
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>
        );
    }

    renderFormulaireRetourDepot() {
        const { classes, reclamation } = this.props;

        const depotGestionValue = this.getCodeDepot();

        return (
            <div className={classes.row}>
                <Typography className={classes.row} variant="h6" gutterBottom>{`Magasin`}</Typography>
                <DepotGestionSelect
                    className={classes.formControl}
                    value={depotGestionValue}
                    valueIsCode
                    onChange={this.handleQualificationDetailsCodeDepot}
                    magasinsSeulement
                    isRequired
                    autoFocus
                    label="Sélectionner un magasin :"
                    modeTri={1}
                    disabled={reclamation.magasin && reclamation.magasin.verrou}
                />
            </div>
        );
    }

    renderFormulaireMagasinRetourATC() {
        const { classes, reclamation } = this.props;

        const magasinRetourATCValue = this.getMagasinRetourATC();

        return (
            <div className={classes.row}>
                <Typography className={classes.row} variant="h6" gutterBottom>{`Magasin`}</Typography>
                <DepotGestionSelect
                    className={classes.formControl}
                    value={magasinRetourATCValue}
                    valueIsCode
                    onChange={this.handleQualificationDetailsMagasinRetourATC}
                    magasinsSeulement
                    isRequired
                    autoFocus
                    label="Sélectionner un magasin :"
                    modeTri={1}
                    disabled={reclamation.magasin && reclamation.magasin.verrou}
                />
            </div>
        );
    }

    renderFormulaireRetourPriseEnCharge() {
        const { classes } = this.props;
        const modePriseEnChargeValue = this.getModePriseEnCharge().toString();

        const plateformeRetourCode = this.getPlateformeRetour();

        const adresse = this.getDetail(ReclamationQualificationDetailCodes.AdresseRetour, 'valeurString');

        return (
            <>
                <div className={classes.row}>
                    <FormControl required className={classes.formControl}>
                        <FormLabel>{modePriseEnChargeLabel}</FormLabel>
                        <RadioGroup id={ReclamationQualificationDetailCodes.ModePriseEnCharge} name={ReclamationQualificationDetailCodes.ModePriseEnCharge} className={classes.group} value={modePriseEnChargeValue} onChange={this.handleQualificationDetailsModePriseEnChargeChange}>
                            {modePriseEnChargeLabels.map((reponse, index) => (
                                <FormControlLabel key={index} value={index.toString()} control={<Radio />} label={reponse} />
                            ))}
                        </RadioGroup>
                    </FormControl>

                    {modePriseEnChargeValue === '0' && (
                        <div className={classes.row} style={{ display: 'flex' }}>
                            <DepotGestionSelect
                                style={{ width: '400px' }}
                                className={classes.formControl}
                                value={plateformeRetourCode}
                                valueIsCode
                                onChange={this.handleQualificationDetailsPlateformeRetour}
                                plateformesSeulement
                                isRequired
                                autoFocus
                                label="Sélectionner une plateforme :"
                                modeTri={1}
                                variant="filled"
                                fullWidth={false}
                            />

                            <Card className={classes.card} style={{ marginLeft: '16px' }}>
                                <CardContent>
                                    <Typography gutterBottom variant="h6">
                                        Adresse de la plateforme selectionnée
                                    </Typography>
                                    {adresse.split('\n').map((ligne, index) => (
                                        <Typography gutterBottom variant="body1" key={index}>
                                            {ligne}
                                        </Typography>
                                    ))}
                                </CardContent>
                            </Card>


                        </div>
                    )}

                </div>


            </>
        );
    }

    renderFormulaireRetourPriseEnChargeATC() {
        const { classes } = this.props;
        const modePriseEnChargeATCValue = this.getModePriseEnChargeATC().toString();

        return (
            <div className={classes.row}>
                <FormControl required className={classes.formControl}>
                    <FormLabel>{modePriseEnChargeATCLabel}</FormLabel>
                    <RadioGroup id={ReclamationQualificationDetailCodes.ModePriseEnChargeATC} name={ReclamationQualificationDetailCodes.ModePriseEnChargeATC} className={classes.group} value={modePriseEnChargeATCValue} onChange={this.handleQualificationDetailsModePriseEnChargeATCChange}>
                        {modePriseEnChargeATCLabels.map((reponse, index) => (
                            <FormControlLabel key={index} value={index.toString()} control={<Radio />} label={reponse} />
                        ))}
                    </RadioGroup>
                </FormControl>
            </div>
        );
    }

    renderRetourConditions() {
        const { classes, reclamation } = this.props;

        const { decote, exclusionDecote, rebutValider } = this.state;

        const retour = reclamation.retour;

        let quantiteProduitsRepris = reclamation.lignes.reduce((previous, current) => previous + Number(current.quantiteAnomalie), 0);

        return (
            <React.Fragment>
                <Typography className={classes.row} variant="h6" gutterBottom>Résumé</Typography>

                {(quantiteProduitsRepris !== 0) &&
                    <Typography className={classes.row} variant="subtitle1" gutterBottom>{`Quantité de produits déclarés : ${quantiteProduitsRepris}`}</Typography>
                }

                {(rebutValider && retour.montantRebut > 0) &&
                    <Typography className={classes.row} variant="subtitle1" gutterBottom>{`Décote des rebuts 100% soit ${retour.montantRebut.toFixed(2)} €`}</Typography>
                }

                <Typography className={classes.row} variant="subtitle1" gutterBottom>
                    {retour.decote === 1 ? `Pas de décote` :
                        `Décote pour retour au-delà de ${retour.decoteJours} jours ${((1 - retour.decote) * 100)}%`}
                </Typography>

                <Typography className={classes.row} variant="subtitle1" gutterBottom>
                    {retour.fdp === 0 ? retour.messageFDP :
                        retour.fdp !== 0 && retour.autoriseJoker && !retour.appliquerFraisRetour ? `Frais de retour : 0€ (annulé)` :
                            `Frais de retour : ${retour.fdp.toFixed(2)} €`}
                </Typography>

                <Typography className={classes.row} variant="subtitle1" gutterBottom>
                    Montant de l'avoir : Pour les clients comptant, il sera précisé sur l'avoir généré par le portail ; pour les clients paiement fin de mois, il sera estimé et communiqué par mail.
                </Typography>

                {(decote !== 0 || exclusionDecote !== 0) && (

                    <Typography className={classes.row} variant="subtitle1" gutterBottom>{`Le taux d'avoir du client est de : ${retour.pctAvoir12Mois} %.`}
                        {retour.autoriseJoker &&
                            <FormControlLabel
                                control={
                                    <Switch
                                        id="joker"
                                        name="joker"
                                        checked={!reclamation.retour.appliquerFraisRetour}
                                        onChange={this.handleJokerFraisRetourChange}
                                        value="ForfaitRetourVolontaire"
                                        color="secondary"
                                    />
                                }
                                label="Je décide d'offrir les frais de retour : "
                                labelPlacement="start"
                            />
                        }
                    </Typography>
                )}
            </React.Fragment>
        );
    }

    renderFormulaireRetourConditions() {
        const { classes } = this.props;

        const { decote, exclusionDecote } = this.state;

        const clientInformeValue = this.getClientInforme();

        return (
            <React.Fragment>

                <Typography className={classes.row} variant="h6" gutterBottom>{`Merci de préciser au client les conditions de retour de la marchandise.`}</Typography>


                {(decote !== 0 || exclusionDecote !== 0) && (

                    <>
                        <Typography className={classes.row} variant="subtitle1" gutterBottom>{`Je confirme que ...`}</Typography>

                        <ul className={classes.row}>
                            <li>
                                Les produits sont en état de revente
                            </li>
                            <li>
                                Les produits sont dans leur emballage d'origine
                            </li>
                            <li>
                                Ni les emballages ni les produits ne sont détériorés, marqués, scotchés, découpés, ...
                            </li>
                            <li>
                                Les produits ne sont pas périssables et ne font pas l'objet d'une date limite de consommation ...
                            </li>
                            <li>
                                Les produits retournés (articles et quantités) sans déclaration et acceptation préalable ne feront pas l'objet d'avoir
                            </li>
                            <li>
                                Le retour physique doit avoir lieu dans les 15 jours suite à la déclaration de ce retour
                            </li>

                        </ul>
                    </>

                )}


                {(decote === 0 && exclusionDecote === 0) && (

                    <>
                        <Typography className={classes.row} variant="subtitle1" gutterBottom>{`La validation du retour ne donnera lieu à aucun avoir client du fait d'une demande de retour effectué au-delà de 90 jours de livraison.`}</Typography>

                    </>

                )}



                <FormControlLabel className={classes.row}
                    control={
                        <Checkbox
                            checked={clientInformeValue}
                            onChange={e => this.handleQualificationDetailsClientInformeChange({ checked: e.target.checked, position: 20, libelle: clientInformeLabel })}
                            value="true"
                            name={ReclamationQualificationDetailCodes.ClientInforme}
                        />
                    }
                    label={clientInformeLabel}
                />
            </React.Fragment>
        );
    }

    renderRebutArticles() {
        const { classes } = this.props;

        const { articlesEnEtatRevente, declaratifRebut, rebutValider } = this.state;

        const articleRebutSelectionne = declaratifRebut.find(x => x.quantiteRebut > 0 && !x.errorQuantiteRebut);
        const articleRebutErreur = declaratifRebut.find(x => x.errorQuantiteRebut);

        return (
            <Box sx={{ mt: 1 }}>
                <Stack direction="column">

                    <FormControl component="fieldset" className={classes.formControl}>
                        <FormLabel component="legend">Les articles sont-ils tous en état de revente ?</FormLabel>
                        <RadioGroup
                            name="ARTICLES_EN_ETAT_DE_REVENTE"
                            className={classes.group}
                            value={articlesEnEtatRevente}
                            onChange={e => this.handleQualificationDetailArticlesEnEtatRevente(e)}
                        >
                            <FormControlLabel className={classes.heightLabel} value="true" control={<Radio />} label="Oui" />
                            <FormControlLabel className={classes.heightLabel} value="false" control={<Radio />} label="Non" />
                        </RadioGroup>
                    </FormControl>

                    {
                        articlesEnEtatRevente === 'false' &&
                        (
                            <Stack
                                direction={'column'}
                                justifyContent={'center'}
                                spacing={2}
                                sx={{ mb: 4, ml: 3 }}
                            >
                                <Typography variant="subtitle1"> Veuillez déclarer les quantités et articles non remettable en stock:</Typography>

                                <Paper>
                                    <TableContainer component={Paper}>
                                        <Table sx={{ minWidth: 300 }} aria-label="simple table">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell align="left">Code Article</TableCell>
                                                    <TableCell align="right">Quantité Déclarée Retour</TableCell>
                                                    <TableCell align="right">Quantité en Rebut</TableCell>
                                                    <TableCell />
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {declaratifRebut.map((article) => (
                                                    <TableRow
                                                        key={`art-${article.codeArticle}`}
                                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                                    >
                                                        <TableCell component="th" scope="row">
                                                            {article.codeArticle}
                                                        </TableCell>

                                                        <TableCell align="right">
                                                            {article.quantiteAnomalie}
                                                        </TableCell>

                                                        <TableCell align="right" sx={{ pb: 0, pt: 0 }}>
                                                            <TextField
                                                                hiddenLabel
                                                                id={`qte-rebut-${article.codeArticle}`}
                                                                sx={{ width: 80 }}
                                                                size="small"
                                                                type="number"
                                                                inputProps={{ min: 0, max: article.quantiteAnomalie }}
                                                                error={article.errorQuantiteRebut}
                                                                onChange={e => this.handleChangeDeclaratifRebut(e, article)}
                                                                value={article.quantiteRebut}
                                                                disabled={rebutValider}
                                                            />
                                                        </TableCell>

                                                        <TableCell>
                                                            {article.errorQuantiteRebut &&
                                                                <Chip variant="outlined" color="error" size="small" label={messageErreurQuantiteRebut} />
                                                            }
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                </Paper>

                                <Stack
                                    direction={'row'}
                                    justifyContent={'flex-end'}
                                    spacing={4}
                                >

                                    <Button
                                        variant="contained"
                                        onClick={(e) => this.handleQualificationDetailArticlesEnEtatRevente(e)}
                                        value={'true'}
                                    >
                                        Annuler
                                    </Button>

                                    <Button
                                        variant="contained"
                                        color='secondary'
                                        disabled={rebutValider || articleRebutSelectionne === undefined || articleRebutErreur}
                                        onClick={() => this.handleValiderArticlesRebut()}
                                    >
                                        Valider
                                    </Button>

                                </Stack>
                            </Stack>
                        )
                    }
                </Stack>
            </Box>
        );
    }

    renderRemboursement() {
        const { classes, reclamation, departPlateforme } = this.props;

        const { remboursementType, repriseMarchandise, declaratifRebut, plvMagasin, eligibilite, rebutValider } = this.state;

        const detailDepot = reclamation.qualificationDetails.find(detail => detail.code === ReclamationQualificationDetailCodes.Depot);

        const montantRebut = declaratifRebut.reduce((previous, current) => previous + Number(current.quantiteRebut * current.prixUnitaire), 0);

        const montant = rebutValider ? (reclamation.retour.montant - montantRebut).toFixed(2) : reclamation.retour.montant.toFixed(2);

        let magasin = '';
        if (detailDepot) {
            magasin = `${detailDepot.valeurObject.nom}`
        }

        let erreurKit = reclamation.lignes.some(x => x.erreurKit);


        /** Gestion des activations et affichages des types de reboursement et reprise marchandise */

        const { remboursement, reprise }  = blocRemboursementManagement(
            {
                autorisation: reclamation.client.autorisationRemboursementEnMagasin,
                montant,
                rebutValider,
                plvMagasin,
                remboursementType,
                eligibilite,

                erreurKit
            }
        );

        const { disabledCB, disabledEspeces, disabledVirement, disabledCarteAchat, showDeductionFinDeMoisFDM } = remboursement;
        const { disabledMagasin, disabledTransfert, disabledPlateformeAvoir, showMessageProduitStocke } = reprise;


        const resultatRemboursement = getResultatRemboursement(
                                        repriseMarchandise,
                                        remboursementType,
                                        eligibilite === EligibiliteRetourVolontaire.OKSeulementMagasin,
                                        declaratifRebut.length > 0,
                                        magasin,
                                        plvMagasin,
                                    );

        return (
            <Box sx={{ width: '100%', mt: 3 }}>
                <Typography className={classes.row} variant="h6" gutterBottom>{`Remboursement`}</Typography>

                <Typography component='div' variant="subtitle1" gutterBottom>
                    <Box fontWeight='500' display='inline'>Type de client : </Box>
                    {getMessageAutorisationRemboursementEnMagasin(reclamation.client.autorisationRemboursementEnMagasin)}
                </Typography>


                <Typography component='div' variant="subtitle1">
                    <Box fontWeight='500' display='inline'>Type de remboursement :</Box>
                </Typography>

                <FormControl component="fieldset" sx={{ width: '100%', pl: 2 }}>
                    <RadioGroup
                        name="TYPE_REMBOURSEMENT"
                        value={remboursementType}
                        onChange={e => this.handleQualificationDetailChangeRemboursementType(e)}
                        sx={{ mb: 1 }}
                    >
                        <Grid container
                            direction='column'
                            justifyContent="flex-start"
                            alignItems="flex-start"
                        >
                            <Grid container
                                direction='row'
                                justifyContent="flex-start"
                                alignItems="center"
                            >
                                <Grid item sx={{ width: '300px' }}>
                                    <Typography variant="subtitle1">- Immédiat sur caisse :</Typography>
                                </Grid>

                                <Grid item xs={'auto'}>
                                    <FormControlLabel
                                        className={classes.heightLabel}
                                        value={RemboursementType.Cb}
                                        control={<Radio />}
                                        label={typeRemboursementLabels[RemboursementType.Cb]}
                                        disabled={disabledCB}
                                    />
                                    <FormControlLabel
                                        className={classes.heightLabel}
                                        value={RemboursementType.Especes}
                                        control={<Radio />}
                                        label={typeRemboursementLabels[RemboursementType.Especes]}
                                        disabled={disabledEspeces}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container
                                direction='row'
                                justifyContent="flex-start"
                                alignItems="center"
                            >
                                <Grid item sx={{ width: '300px' }}>
                                    <Typography variant="subtitle1">- Par la compta :</Typography>
                                </Grid>

                                <Grid item xs={'auto'}>
                                    <FormControlLabel
                                        className={classes.heightLabel}
                                        value={RemboursementType.Virement}
                                        control={<Radio />}
                                        label={typeRemboursementLabels[RemboursementType.Virement]}
                                        disabled={disabledVirement}
                                    />
                                    <FormControlLabel
                                        className={classes.heightLabel}
                                        value={RemboursementType.CarteAchat}
                                        control={<Radio />}
                                        label={typeRemboursementLabels[RemboursementType.CarteAchat]}
                                        disabled={disabledCarteAchat}
                                    />
                                </Grid>

                            </Grid>


                            {showDeductionFinDeMoisFDM &&
                                (
                                    <Grid container
                                        direction='row'
                                        justifyContent="flex-start"
                                        alignItems="center"
                                    >
                                        <Grid item sx={{ width: '300px' }}>
                                            <Typography variant="subtitle1">- Déduction sur la prochaine facture :</Typography>
                                        </Grid>

                                        <Grid item xs={'auto'}>
                                            <FormControlLabel
                                                className={classes.heightLabel}
                                                value={RemboursementType.DeductionFinMois}
                                                control={<Radio />}
                                                label=""
                                            />
                                        </Grid>

                                    </Grid>
                                )}

                            {showDeductionFinDeMoisFDM && reclamation.client.autorisationRemboursementEnMagasin === AutorisationRemboursementEnMagasin.NonAutorise && (
                                <Grid container
                                    direction='row'
                                    justifyContent="flex-start"
                                    alignItems="center"
                                    sx={{ mt: 1, mb: 1 }}
                                >
                                    <Grid item>
                                        <AideMessage
                                            message="Si le client a payé comptant et souhaite un remboursement comptant, contactez la comptabilité"
                                            icon="⚠️"
                                        />
                                    </Grid>
                                </Grid>
                            )}

                        </Grid>
                    </RadioGroup>
                </FormControl>


                <Typography component='div' variant="subtitle1">
                    <Box fontWeight='500' display='inline'>Reprise de la marchandise : </Box>
                    {showMessageProduitStocke && (
                        <Chip icon={<InfoIcon />}
                            label="Produit stocké en magasin à remettre en stock, interdiction de renvoyer au Monné"
                            variant="outlined"
                            color="info"
                        />
                    )}
                </Typography>

                <FormControl component="fieldset" sx={{ width: '100%', pl: 2 }}>
                    <RadioGroup
                        name="REPRISE_MARCHANDISE"
                        value={repriseMarchandise}
                        onChange={e => this.handleQualificationDetailChangeRepriseMarchandise(e)}
                        sx={{ mb: 1 }}
                    >
                        <Grid container
                            direction='column'
                            justifyContent="flex-start"
                            alignItems="flex-start"
                        >
                            <Grid container
                                direction='row'
                                justifyContent="flex-start"
                                alignItems="center"
                            >
                                <Grid item sx={{ width: '230px' }}>
                                    <Typography variant="subtitle1">- Sur le magasin :</Typography>
                                </Grid>

                                <Grid item xs={'auto'}>
                                    <FormControlLabel
                                        className={classes.heightLabel}
                                        value={RepriseMarchandiseType.Magasin}
                                        control={<Radio />}
                                        label={typeRepriseMarchandiseLabels[RepriseMarchandiseType.Magasin]}
                                        disabled={disabledMagasin}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container
                                direction='row'
                                justifyContent="flex-start"
                                alignItems="center"
                            >
                                <Grid item sx={{ width: '230px' }}>
                                    <Typography variant="subtitle1">- Transfert Plateforme :</Typography>
                                </Grid>

                                <Grid item xs={'auto'}>
                                    <FormControlLabel
                                        className={classes.heightLabel}
                                        value={RepriseMarchandiseType.Transfert}
                                        control={<Radio />}
                                        label={typeRepriseMarchandiseLabels[RepriseMarchandiseType.Transfert]}
                                        disabled={disabledTransfert}
                                    />
                                </Grid>

                            </Grid>

                            <Grid container
                                direction='row'
                                justifyContent="flex-start"
                                alignItems="center"
                            >
                                <Grid item sx={{ width: '230px' }}>
                                    <Typography variant="subtitle1">- En Plateforme pour avoir :</Typography>
                                </Grid>

                                <Grid item xs={'auto'}>
                                    <FormControlLabel
                                        className={classes.heightLabel}
                                        value={RepriseMarchandiseType.PlateformeAvoir}
                                        control={<Radio />}
                                        label={typeRepriseMarchandiseLabels[RepriseMarchandiseType.PlateformeAvoir]}
                                        disabled={disabledPlateformeAvoir}
                                    />
                                </Grid>

                            </Grid>

                        </Grid>
                    </RadioGroup>
                </FormControl>

                <Typography component='div' variant="subtitle1" gutterBottom>
                    <Box fontWeight='500' display='inline'>Résultat : </Box>
                    {resultatRemboursement}
                </Typography>

            </Box >
        )
    }   


    mainDescriptor = [
        {
            name: 'header',
            renderer: this.renderHeader,
        },
        {
            name: 'formulaire',
            renderer: this.renderPieceGestionSearch,
            getter: () => {
                const aTester = {
                    numeroPiece: this.props.reclamation.numeroPiece,
                    etape: this.state.etape,
                    agePiece: this.props.agePiece,
                    decoteRetourExclu: this.props.reclamation.client.decoteRetourExclu,
                    ignorerDecoteTotale: this.state.ignorerDecoteTotale,
                    // DEV-4012 : type de retour choisi (null tant que non choisi).
                    typeRetour: this.state.typeRetour
                };
                return aTester;
            },
            subComponents: [

                // DEV-4012 : bloc « Type de retour » affiché dès qu'un BL est sélectionné.
                {
                    tester: a => a.numeroPiece !== '',
                    descriptor: [{ renderer: this.renderTypeRetour }]
                },
                {
                    tester: a => a.numeroPiece !== '' && a.typeRetour !== null && a.etape > Etape.ChoixDeLaPiece && (a.agePiece < 90 || a.decoteRetourExclu || a.ignorerDecoteTotale),
                    descriptor: [
                        { renderer: this.renderLigneGestionQualification },
                        { renderer: this.renderTraitementRetour }
                    ]

                },
                {
                    tester: a => a.numeroPiece !== '' && a.typeRetour !== null && a.etape > Etape.ChoixDeLaPiece && (a.agePiece >= 90 && !a.decoteRetourExclu) && !a.ignorerDecoteTotale,
                    descriptor: [{ renderer: this.renderWarningAgePieceSansExclusionDecote }]
                }
            ]

        }
    ];

    render() {
        return qualificationHelper.getComponents(this.mainDescriptor);
    }

}

DemandeRetourMarchandiseLivreeConformeStep.propTypes = {
    reclamation: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    onCheckErrors: PropTypes.func.isRequired,
    onDetailChange: PropTypes.func.isRequired,
    onSelectPiece: PropTypes.func.isRequired,
    onAddLigne: PropTypes.func.isRequired,
    onRemoveLigne: PropTypes.func.isRequired,
    onSelectAllLignes: PropTypes.func.isRequired,
    validationErrors: PropTypes.array.isRequired,
    onNextStepValid: PropTypes.func.isRequired,
    namespace: PropTypes.string.isRequired,
    onAddRetour: PropTypes.func.isRequired,
    onRemoveRetour: PropTypes.func.isRequired,
    onClickCheckEligibilite: PropTypes.func.isRequired,

    utilisateurMagasin: PropTypes.bool.isRequired,
    departPlateforme: PropTypes.bool.isRequired,
};

export default withStyles(styles)(DemandeRetourMarchandiseLivreeConformeStep);
